import {
  ArrowRight,
  CalendarDays,
  Check,
  Clock3,
  Copy,
  ExternalLink,
  Facebook,
  FileText,
  FileVideo,
  Globe2,
  GraduationCap,
  ImagePlus,
  Instagram,
  LogOut,
  Mail,
  MapPin,
  MessageCircle,
  Music2,
  ShieldCheck,
  PlayCircle,
  Plane,
  Save,
  Search,
  Trash2,
  Trophy,
  UploadCloud,
  UserRound,
  Users,
  WandSparkles,
  X,
  Youtube
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, FormEvent, ReactNode } from "react";
import academyLibrary from "../data/water-polo-academy-library.json";
import contactChannels from "../data/consultation-channels.json";
import contentCardsData from "../data/content-cards.json";
import sourceLibrary from "../data/water-polo-source-library.json";
import {
  waterPoloRuleSections,
  waterPoloRulesMeta,
  waterPoloSignalCards,
  waterPoloSignalPrincipleHtml,
  waterPoloWhistleNoticeHtml,
  waterPoloWhistlePrincipleHtml,
  waterPoloWhistleRows,
  type WaterPoloRuleClause,
  type WaterPoloRuleSection
} from "./waterPoloRulesData";
import type { Article, ContentCard, ContentColumnId, DomesticVideo, FeedCategory, FeedItem, ScrapePayload, VideoCategory, VideoPlaybackInfo, VideoStorageInfo } from "./types";

type PageKey =
  | "home"
  | "news"
  | "youth"
  | "youth-athletes"
  | "youth-detail"
  | "resources"
  | "resource-detail"
  | "clubs"
  | "club-detail"
  | "policies"
  | "international-training"
  | "consultation"
  | "about"
  | "membership"
  | "member-login"
  | "content-card"
  | "calendar"
  | "notices"
  | "live-competitions"
  | "domestic"
  | "global"
  | "event-videos"
  | "video"
  | "domestic-videos"
  | "global-videos"
  | "europe-videos"
  | "video-player"
  | "video-admin"
  | "article"
  | "article-admin";

type SiteMembershipTier = "standard" | "premium";

interface SiteMemberUser {
  id: string;
  account: string;
  email?: string;
  displayName: string;
  membershipTier: SiteMembershipTier;
}

type NavigateHandler = (page: PageKey, targetPath?: string) => void;

const searchCopy = {
  search: "搜索",
  searchTitle: "站内搜索",
  searchPlaceholder: "搜索赛事、俱乐部、升学政策咨询、规则或视频",
  searchEmpty: "输入关键词后显示匹配内容。"
};

const categories: Array<"全部" | Extract<FeedCategory, "赛事" | "赛程">> = ["全部", "赛事", "赛程"];

interface NavItem {
  key?: PageKey;
  label: string;
  path: string;
  external?: boolean;
  activePages?: PageKey[];
  children?: NavItem[];
}

const navItems: NavItem[] = [
  { key: "home", label: "首页", path: "/" },
  { key: "calendar", label: "赛事", path: "/calendar" },
  {
    label: "青少年水球",
    path: "/youth",
    activePages: ["youth", "youth-athletes", "youth-detail"],
    children: [
      { key: "youth", label: "青少年水球首页", path: "/youth", activePages: ["youth", "youth-detail"] },
      { key: "youth-athletes", label: "青少年运动员", path: "/youth/athletes" }
    ]
  },
  {
    key: "clubs",
    label: "俱乐部",
    path: "/clubs",
    activePages: ["clubs", "club-detail"]
  },
  {
    key: "international-training",
    label: "国内外训练营",
    path: "/international-training/",
    activePages: ["international-training"]
  }
];

const utilityNavItems = [
  { label: "最新", href: "/news" },
  { label: "会员", href: "/membership" },
  { label: "水球规则", href: "/resources/rules" },
  { label: "联系我们", href: "/consultation" }
];

function WhatsAppIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      aria-hidden="true"
      className="social-whatsapp-icon"
      focusable="false"
      height={size}
      viewBox="0 0 24 24"
      width={size}
    >
      <path
        d="M12 2.25a9.45 9.45 0 0 0-8.05 14.4L2.75 21.2l4.67-1.13A9.45 9.45 0 1 0 12 2.25Zm0 1.85a7.6 7.6 0 1 1-3.88 14.13l-.42-.25-2.34.57.6-2.28-.28-.44A7.6 7.6 0 0 1 12 4.1Z"
        fill="currentColor"
      />
      <path
        d="M8.15 7.65c.16-.35.33-.36.5-.36h.43c.14 0 .33.05.5.38.2.4.64 1.55.7 1.67.06.12.1.27.02.43-.1.18-.15.27-.29.42l-.34.37c-.1.1-.22.23-.1.44.13.22.57.94 1.24 1.52.84.75 1.56.98 1.8 1.1.23.12.38.1.52-.06.15-.17.6-.7.76-.93.16-.22.32-.18.54-.1.22.08 1.42.67 1.66.8.24.11.4.17.46.27.06.1.06.61-.15 1.2-.22.58-1.24 1.12-1.74 1.16-.45.04-1.03.06-1.66-.1-.38-.1-.88-.28-1.52-.55-2.66-1.15-4.4-3.82-4.54-4-.13-.18-1.08-1.43-1.08-2.74 0-1.3.68-1.94.93-2.2Z"
        fill="currentColor"
      />
    </svg>
  );
}

const socialNavItems = [
  { label: "Facebook", href: "https://www.facebook.com/", icon: <Facebook size={18} /> },
  { label: "X", href: "https://x.com/", icon: <span className="social-icon-text">X</span> },
  { label: "Instagram", href: "https://www.instagram.com/", icon: <Instagram size={18} /> },
  { label: "YouTube", href: "https://www.youtube.com/", icon: <Youtube size={19} /> },
  { label: "TikTok", href: "https://www.tiktok.com/", icon: <Music2 size={18} /> },
  { label: "WhatsApp", href: "https://www.whatsapp.com/", icon: <WhatsAppIcon size={18} /> },
  { label: "小红书", href: "https://www.xiaohongshu.com/", icon: <span className="social-icon-text social-icon-wide">小红书</span> },
  { label: "B站", href: "https://www.bilibili.com/", icon: <span className="social-icon-text">B站</span> }
];

const liveCompetitionFallbacks = [
  {
    title: "全球水球赛事动态",
    meta: "World Aquatics",
    time: "最新",
    location: "国际水球",
    href: "/news"
  },
  {
    title: "国内水球赛事日历",
    meta: "赛事日历",
    time: "日历",
    location: "中国大陆",
    href: "/calendar"
  },
  {
    title: "赛事视频精选",
    meta: "赛事视频",
    time: "视频",
    location: "国内外赛事",
    href: "/videos"
  },
  {
    title: "青少年水球赛事",
    meta: "青少年水球",
    time: "青年",
    location: "U 系列赛事",
    href: "/youth"
  },
  {
    title: "水球俱乐部动态",
    meta: "俱乐部",
    time: "俱乐部",
    location: "国内外俱乐部",
    href: "/clubs"
  },
  {
    title: "国内外训练营资讯",
    meta: "训练营",
    time: "训练",
    location: "海外与国内",
    href: "/international-training/"
  }
];

const liveCompetitionTimes = ["23:00", "01:00", "05:00", "06:30", "18:00", "22:00"];

interface LiveCompetitionCard {
  title: string;
  phase: string;
  eventTitle: string;
  time: string;
  date: string;
  href: string;
}

const pagePaths: Record<PageKey, string> = {
  home: "/",
  news: "/news",
  youth: "/youth",
  "youth-athletes": "/youth/athletes",
  "youth-detail": "/youth",
  resources: "/resources/rules",
  "resource-detail": "/resources/rules",
  clubs: "/clubs",
  "club-detail": "/clubs/taiyuan-xuefu",
  policies: "/policies",
  "international-training": "/international-training/",
  consultation: "/consultation",
  about: "/about",
  membership: "/membership",
  "member-login": "/member-login",
  "content-card": "/library",
  calendar: "/calendar",
  notices: "/notices",
  "live-competitions": "/live-competitions",
  domestic: "/",
  global: "/",
  "event-videos": "/videos",
  video: "/video",
  "domestic-videos": "/videos/domestic",
  "global-videos": "/videos/global",
  "europe-videos": "/videos/europe",
  "video-player": "/videos/watch",
  "video-admin": "/admin/videos",
  article: "/articles",
  "article-admin": "/admin/articles"
};

const videoCategoryLabels: Record<VideoCategory, string> = {
  domestic: "国内赛事",
  global: "全球赛事",
  europe: "欧洲俱乐部赛事"
};

interface VideoEntryCard {
  category: VideoCategory;
  title: string;
  source: string;
  summary: string;
  url: string;
  page: PageKey;
}

const videoCards: VideoEntryCard[] = [
  {
    category: "domestic",
    title: "国内赛事",
    source: "中国大陆赛事",
    summary: "全国冠军赛、锦标赛、U 系列与俱乐部赛事视频。",
    url: pagePaths["domestic-videos"],
    page: "domestic-videos"
  },
  {
    category: "global",
    title: "全球赛事",
    source: "World Aquatics",
    summary: "世界杯、世锦赛、奥运周期赛事与技术集锦。",
    url: pagePaths["global-videos"],
    page: "global-videos"
  },
  {
    category: "europe",
    title: "欧洲俱乐部赛事",
    source: "European Aquatics",
    summary: "欧冠、欧洲杯和高水平俱乐部比赛画面。",
    url: pagePaths["europe-videos"],
    page: "europe-videos"
  }
];

interface InformationSourceLogo {
  name: string;
  href: string;
  src: string;
  alt: string;
  tone: "light" | "dark";
}

interface InformationSourceRecord {
  name: string;
  primaryUrl: string;
  logo?: {
    publicPath: string;
    alt: string;
    tone?: "light" | "dark";
  };
}

interface ContentSourceLink {
  label: string;
  url: string;
}

interface AcademyBodyBlock {
  heading: string;
  paragraphs: string[];
}

interface DetailGroup {
  heading: string;
  items: string[];
}

interface AcademySection {
  id: string;
  title: string;
  eyebrow: string;
  summary: string;
  image: string;
  imageAlt: string;
  imageSourceName: string;
  imageSourceUrl: string;
  highlights: string[];
  body: AcademyBodyBlock[];
  detailGroups: DetailGroup[];
  actions: string[];
  sources: ContentSourceLink[];
}

interface YouthKnowledgeSection {
  id: string;
  title: string;
  summary: string;
  image: string;
  imageAlt: string;
  imageSourceName: string;
  imageSourceUrl: string;
  facts: string[];
  detailGroups: DetailGroup[];
  sources: ContentSourceLink[];
}

interface AcademyLibrary {
  academySections: AcademySection[];
  youthSections: YouthKnowledgeSection[];
}

interface StaticSearchEntry {
  id: string;
  title: string;
  summary: string;
  meta: string;
  url: string;
  keywords: string[];
}

interface BusinessLandingCard {
  title: string;
  summary: string;
  path: string;
}

interface BusinessDetailGroup {
  heading: string;
  items: string[];
}

interface BusinessDetailSection {
  path: string;
  kicker: string;
  title: string;
  summary: string;
  notice: string;
  details: BusinessDetailGroup[];
}

const informationSourceLogos: InformationSourceLogo[] = (
  sourceLibrary.prioritySources as InformationSourceRecord[]
)
  .filter((source) => Boolean(source.logo?.publicPath))
  .map((source) => ({
    name: source.name,
    href: source.primaryUrl,
    src: source.logo?.publicPath ?? "",
    alt: source.logo?.alt ?? source.name,
    tone: source.logo?.tone ?? "light"
  }));

const academyContent = academyLibrary as AcademyLibrary;
const contentCards = contentCardsData as ContentCard[];

const staticSearchEntries: StaticSearchEntry[] = [
  {
    id: "clubs",
    title: "水球俱乐部",
    summary: "国内外水球俱乐部信息入口，展示已入驻、合作中和待核验的俱乐部资源。",
    meta: "栏目入口",
    url: "/clubs",
    keywords: ["入驻俱乐部", "国内俱乐部", "国际俱乐部", "训练场地", "教练"]
  },
  {
    id: "taiyuan-xuefu",
    title: "太原学府俱乐部",
    summary: "入驻水球学院网站的太原青少年水球俱乐部，具体信息以俱乐部详情页和实际咨询确认为准。",
    meta: "入驻俱乐部",
    url: "/clubs/taiyuan-xuefu",
    keywords: ["山西", "太原", "青少年", "9-13 岁", "俱乐部"]
  },
  {
    id: "policies",
    title: "升学政策咨询",
    summary: "整理国内公开政策、学校选择路径和国际申请基础，并提供进一步咨询入口，不承诺升学或录取结果。",
    meta: "栏目入口",
    url: "/policies",
    keywords: ["政策", "升学", "咨询", "学校", "运动员等级", "国际申请"]
  },
  {
    id: "international-training",
    title: "国内外训练营",
    summary: "国内外训练营、交流比赛、出境准备的信息入口。",
    meta: "栏目入口",
    url: "/international-training/",
    keywords: ["国内训练营", "海外训练", "国际训练", "海外训练营", "交流赛", "欧洲俱乐部"]
  },
  {
    id: "tactics-teaching",
    title: "水球攻防战术",
    summary: "无需登录即可浏览和播放 6 个进攻及 2 个防守基础战术；设计或保存个人战术时再注册登录。",
    meta: "会员教学工具",
    url: "/member/tactics",
    keywords: ["会员", "高级会员", "普通会员", "战术", "攻防", "教学板", "个人战术库", "跑位", "传球"]
  },
  {
    id: "consultation",
    title: "联系我们",
    summary: "通过微信（WeChat）或邮箱联系水球学院。",
    meta: "服务入口",
    url: "/consultation",
    keywords: ["微信", "WeChat", "邮箱", "邮件", "联系"]
  },
  {
    id: "about-water-polo-academy",
    title: "了解水球学院",
    summary: "水球学院是面向中国家庭的水球信息、知识、俱乐部和国际训练资源连接平台。",
    meta: "品牌介绍",
    url: "/about",
    keywords: ["水球学院", "品牌介绍", "平台定位", "资源连接"]
  },
  {
    id: "membership",
    title: "注册会员",
    summary: "注册会员服务入口，面向家庭、俱乐部和国际训练需求开放深度资料与资源匹配。",
    meta: "会员服务",
    url: "/membership",
    keywords: ["会员", "注册会员", "家庭会员", "俱乐部会员", "国际训练会员"]
  },
  ...contentCards.map((card) => ({
    id: `content-card-${card.id}`,
    title: card.title,
    summary: card.summary,
    meta: contentColumnLabel(card.columnId),
    url: contentCardPath(card),
    keywords: [...card.tags, card.sourceName, card.region]
  })),
  ...academyContent.academySections.map((section) => ({
    id: `knowledge-${section.id}`,
    title: section.title,
    summary: section.summary,
    meta: "水球知识",
    url: resourceSectionPath(section),
    keywords: [section.eyebrow, ...section.highlights]
  })),
  ...academyContent.youthSections.map((section) => ({
    id: `youth-${section.id}`,
    title: section.title,
    summary: section.summary,
    meta: "青少年水球",
    url: youthSectionPath(section),
    keywords: section.facts
  }))
];

const clubDirectorySections: BusinessDetailSection[] = [
  {
    path: "/clubs/domestic",
    kicker: "俱乐部库",
    title: "国内俱乐部",
    summary: "后续按城市整理国内青少年水球俱乐部，先建立统一字段，再逐步补齐北京、上海、广州、深圳、成都、杭州等重点城市。",
    notice: "每个俱乐部页面必须明确关系状态：入驻展示、已确认合作、公开信息整理或待核验，不能默认写成水球学院旗下机构。",
    details: [
      {
        heading: "统一采集字段",
        items: [
          "俱乐部正式名称、所在城市、训练场地名称和地址。",
          "适合年龄段、分层课程、训练时间、是否有比赛队。",
          "教练姓名、资质、执教经历和可公开照片。",
          "场地照片、泳池规格、器材、淋浴更衣等基础设施。",
          "联系方式、咨询入口、信息来源和最后核验日期。"
        ]
      },
      {
        heading: "第一阶段城市",
        items: [
          "第一阶段先完善已入驻俱乐部资料页，再逐步扩展重点城市。",
          "下一批可按用户咨询量和场地资源，优先补北京、上海、广州、深圳、成都、杭州。",
          "每个城市先做公开信息页，确认合作后再升级为可咨询页面。"
        ]
      }
    ]
  },
  {
    path: "/clubs/international",
    kicker: "俱乐部库",
    title: "国际俱乐部",
    summary: "整理海外公开俱乐部和训练体系，为未来外训、交流比赛和俱乐部合作建立信息底座。",
    notice: "国际俱乐部信息必须以官方网页、协会页面或赛事页面为主，不能把未确认联系人写成正式合作方。",
    details: [
      {
        heading: "采集重点",
        items: [
          "俱乐部所在国家、城市、官网、训练年龄段和队伍层级。",
          "是否公开提供青少年训练、训练营、试训或交流机会。",
          "赛事参与情况、所属联赛或协会、公开联系人。",
          "费用、日期、语言、住宿、监护和保险要求。",
          "信息来源链接、抓取日期和人工核验状态。"
        ]
      },
      {
        heading: "使用边界",
        items: [
          "国际俱乐部页面只做信息呈现和联系线索整理。",
          "没有书面确认前，不写官方合作、试训结果承诺或录取结果承诺等表述。",
          "面向家长时，必须把签证、监护、保险和费用作为独立风险提示。"
        ]
      }
    ]
  },
  {
    path: "/clubs/cooperation",
    kicker: "合作入口",
    title: "俱乐部合作",
    summary: "为国内俱乐部与海外俱乐部建立联系，未来可承接交流训练、联合训练营、教练交流和赛事互访。",
    notice: "合作页的目标是建立真实连接，不用营销话术替代合作条件、资源边界和责任划分。",
    details: [
      {
        heading: "合作方向",
        items: [
          "国内俱乐部展示：场地、教练、学员年龄段、比赛队基础。",
          "海外资源连接：训练营、短期外训、交流赛、教练课程。",
          "裁判和规则资源：邀请专业裁判做规则培训、比赛执裁交流。",
          "家长服务：训练路径评估、出境准备清单和费用边界说明。"
        ]
      },
      {
        heading: "落地步骤",
        items: [
          "先完成俱乐部资料页和咨询入口。",
          "再建立合作意向表，记录俱乐部需求和可提供资源。",
          "最后形成可公开发布的合作项目页。"
        ]
      }
    ]
  },
  {
    path: "/clubs/submit",
    kicker: "俱乐部入驻",
    title: "提交俱乐部信息",
    summary: "为希望进入水球学院网站展示的俱乐部预留资料提交入口，正式上线前先按统一字段准备材料。",
    notice: "提交信息不等于合作确认。俱乐部名称、场地、教练、联系方式和授权材料必须经过人工核验后才能公开展示。",
    details: [
      {
        heading: "需要准备的资料",
        items: [
          "俱乐部正式名称、所在城市、训练场地名称和地址。",
          "适合年龄段、课程分层、训练时间、比赛队路径和参赛情况。",
          "教练姓名、资质、执教经历，以及可公开使用的照片授权。",
          "场地和训练图片、设施说明、联系方式和咨询渠道授权。",
          "关系状态说明：入驻展示、信息收录、合作意向或已确认合作。"
        ]
      },
      {
        heading: "审核边界",
        items: [
          "没有书面合作文件前，不使用官方直属、授权基地等表述。",
          "涉及未成年人照片、联系方式和费用信息，必须单独确认公开授权。",
          "审核通过后进入俱乐部资料库，再根据资料完整度和服务匹配度决定是否进入首页推荐。"
        ]
      }
    ]
  }
];

const policyLandingCards: BusinessLandingCard[] = [
  {
    title: "国内政策库",
    summary: "整理国家、地方、学校和体育系统公开政策，所有信息必须保留来源和核验日期。",
    path: "/policies/domestic"
  },
  {
    title: "运动员等级",
    summary: "梳理水球运动员等级、赛事成绩和申报材料之间的关系，避免把条件写成承诺。",
    path: "/policies/athlete-level"
  },
  {
    title: "学校选择路径",
    summary: "围绕孩子年龄、城市、训练基础和学校资源，帮助家长理解可探索的路径。",
    path: "/policies/school-path"
  },
  {
    title: "国际申请材料说明",
    summary: "说明水球履历可如何作为活动和体育经历材料，同时明确不能替代学术、语言和学校要求。",
    path: "/policies/international-application"
  }
];

const policyDetailSections: BusinessDetailSection[] = [
  {
    path: "/policies/domestic",
    kicker: "政策与升学",
    title: "国内政策库",
    summary: "这个栏目用于收集和解释国内公开政策，帮助家长判断水球训练、比赛成绩、学校选择之间可能存在的关系。",
    notice: "政策库只做公开信息整理，不承诺任何学校录取、升学资格或优待结果。",
    details: [
      {
        heading: "需要持续抓取的政策类型",
        items: [
          "教育部门发布的体育特长、体教融合、校园体育相关文件。",
          "体育部门发布的青少年赛事、运动员等级、注册参赛相关通知。",
          "学校公开发布的招生简章、体育项目招生说明和报名条件。",
          "地方协会、学校或俱乐部公开发布的赛事成绩认定规则。",
          "政策原文、发布日期、适用地区、有效期和最后核验日期。"
        ]
      },
      {
        heading: "页面写法要求",
        items: [
          "先列政策事实，再解释对家庭的可能影响。",
          "把政策适用范围、限制条件和不确定性写清楚。",
          "每条信息都要附来源链接，不能只写二手转述。"
        ]
      }
    ]
  },
  {
    path: "/policies/athlete-level",
    kicker: "政策与升学",
    title: "运动员等级",
    summary: "解释水球运动员等级、赛事成绩、申报材料之间的关系，让家长知道哪些信息需要提前准备。",
    notice: "运动员等级申报有具体赛事、成绩、材料和审核要求，页面不能把训练参与直接写成等级结果。",
    details: [
      {
        heading: "需要说明的核心内容",
        items: [
          "等级称号类型和对应主管部门公开规则。",
          "哪些赛事成绩可能与等级申报有关。",
          "成绩证明、秩序册、成绩册、参赛证件等材料要求。",
          "申报时间、审核流程、常见退回原因和核验方式。",
          "水球项目与其他体育项目在公开规则上的差异。"
        ]
      },
      {
        heading: "家长准备清单",
        items: [
          "保留孩子参赛通知、报名记录、成绩证明和照片。",
          "确认赛事是否在公开认可范围内。",
          "提前区分训练记录、参赛经历和可用于申报的成绩材料。"
        ]
      }
    ]
  },
  {
    path: "/policies/school-path",
    kicker: "政策与升学",
    title: "学校选择路径",
    summary: "从孩子年龄、城市、训练基础、比赛经历和学校体育资源出发，梳理更现实的学校选择路径。",
    notice: "学校选择是综合决策，水球只能作为孩子成长履历和体育能力的一部分，不能替代学业基础。",
    details: [
      {
        heading: "路径分析维度",
        items: [
          "孩子当前年龄、游泳基础、水球训练年限和身体条件。",
          "所在城市是否有长期训练场地、稳定教练和比赛机会。",
          "目标学校是否公开支持水球、游泳或综合体育项目。",
          "孩子是否有稳定参赛记录、队伍经历和可展示训练档案。",
          "家庭是否能承担训练、比赛、外训和时间管理成本。"
        ]
      },
      {
        heading: "输出给家长的结果",
        items: [
          "可探索学校清单，而不是录取承诺。",
          "未来 6-12 个月训练与比赛建议。",
          "需要补充的材料、成绩和沟通对象。"
        ]
      }
    ]
  },
  {
    path: "/policies/international-application",
    kicker: "政策与升学",
    title: "国际申请材料说明",
    summary: "解释水球经历可如何整理为活动和体育履历材料，以及它不能替代的硬性申请要求。",
    notice: "国际申请结果取决于学术、语言、学校匹配、申请材料和招生政策，体育经历只是综合履历的一部分，是否被采纳以学校要求为准。",
    details: [
      {
        heading: "水球履历可整理的材料",
        items: [
          "连续训练记录、训练频率、队伍角色和教练反馈。",
          "正式比赛记录、年龄组、赛事名称、成绩证明和视频资料。",
          "团队项目中的协作、沟通、规则意识和责任承担材料。",
          "海外训练或交流经历的项目说明、时间、地点和参与证明。",
          "裁判规则学习、志愿服务或队伍管理等补充经历。"
        ]
      },
      {
        heading: "仍然必须满足的要求",
        items: [
          "学校明确的学术成绩和语言要求。",
          "申请文书、推荐信、面试和材料截止时间。",
          "项目是否招收对应年龄、性别和水平的水球学生。",
          "家庭预算、签证、监护和保险安排。"
        ]
      }
    ]
  }
];

const internationalLandingCards: BusinessLandingCard[] = [
  {
    title: "海外训练营",
    summary: "合并整理海外俱乐部训练、试训、寒暑假训练营和短期外训项目。",
    path: "/international-training/clubs-camps"
  },
  {
    title: "交流比赛",
    summary: "关注公开报名或邀请制交流赛事，明确是否已核验。",
    path: "/international-training/competitions"
  },
  {
    title: "出境准备",
    summary: "提示签证、保险、监护、安全、语言和费用边界。",
    path: "/international-training/preparation"
  },
  {
    title: "合作网络",
    summary: "整理可对接的海外俱乐部、赛事组织方、教练和合作线索。",
    path: "/international-training/network"
  }
];

const internationalDetailSections: BusinessDetailSection[] = [
  {
    path: "/international-training/clubs-camps",
    kicker: "国际训练",
    title: "海外训练营",
    summary: "建立海外训练营机会库，帮助家庭比较公开训练、试训、寒暑假训练营和短期外训项目。",
    notice: "海外训练营信息必须标注来源和核验状态，没有确认前不写成合作项目或保证名额。",
    details: [
      {
        heading: "机会资料字段",
        items: [
          "国家、城市、俱乐部官网和所属协会或联赛。",
          "青少年队伍年龄段、训练层级、训练季和公开报名方式。",
          "是否提供短期训练、试训、夏令营或国际交流项目。",
          "营期日期、持续天数、每日训练时长和训练主题。",
          "联系人、语言、费用、住宿、退改和监护要求。",
          "官方来源链接、抓取日期、核验状态和风险提示。"
        ]
      },
      {
        heading: "筛选优先级",
        items: [
          "优先选择官网信息清楚、青少年体系完整的俱乐部。",
          "优先收录有公开训练营或国际交流经验的机构。",
          "优先核验裁判资源熟悉的欧洲俱乐部体系。"
        ]
      },
      {
        heading: "家长决策提示",
        items: [
          "先判断孩子是否具备连续训练和英文沟通基础。",
          "再比较训练质量、监护安全和总体预算。",
          "最后确认签证、机票、保险和当地交通。"
        ]
      }
    ]
  },
  {
    path: "/international-training/competitions",
    kicker: "国际训练",
    title: "交流比赛",
    summary: "关注海外青少年水球交流赛、俱乐部邀请赛和训练赛机会，为孩子建立真实比赛经历。",
    notice: "交流比赛是否能参加，取决于主办方规则、年龄组、队伍名额、报名资格和出行条件。",
    details: [
      {
        heading: "赛事信息字段",
        items: [
          "赛事名称、主办方、举办城市、日期和年龄组。",
          "报名方式、队伍要求、是否接受国际队伍或个人报名。",
          "比赛规则、场次安排、裁判体系和安全保障。",
          "费用、住宿、交通、保险和监护安排。",
          "公开来源、邀请函状态、核验日期和报名截止时间。"
        ]
      },
      {
        heading: "与训练路径的关系",
        items: [
          "交流比赛可以检验孩子在对抗、规则和团队配合上的真实水平。",
          "比赛经历应形成训练档案，包括参赛证明、照片、视频和教练反馈。",
          "不把单次比赛包装成升学或入选保证。"
        ]
      }
    ]
  },
  {
    path: "/international-training/preparation",
    kicker: "国际训练",
    title: "出境准备",
    summary: "为海外训练和比赛建立出境清单，降低家长在签证、监护、保险、安全和费用上的信息不确定性。",
    notice: "出境准备属于高责任信息，页面要提示家长以官方签证、保险和主办方要求为准。",
    details: [
      {
        heading: "准备清单",
        items: [
          "护照、签证、邀请函、学校假期安排和出入境材料。",
          "未成年人监护授权、家长陪同方案和紧急联系人。",
          "运动保险、医疗保险、既往病史和常用药品。",
          "训练装备、比赛装备、泳帽、护齿、拖鞋和恢复用品。",
          "费用预算、付款节点、汇率、退改规则和备用金。"
        ]
      },
      {
        heading: "安全边界",
        items: [
          "明确孩子是否由家长、教练或当地机构监护。",
          "明确住宿、交通、训练场地和紧急就医流程。",
          "所有外训项目上线前都要有人工核验记录。"
        ]
      }
    ]
  },
  {
    path: "/international-training/network",
    kicker: "国际训练",
    title: "合作网络",
    summary: "为国内俱乐部、海外俱乐部、训练营、赛事组织方、教练和裁判资源建立合作线索库。",
    notice: "合作网络只记录已公开或已授权的信息，没有确认前不写成正式合作关系。",
    details: [
      {
        heading: "合作线索字段",
        items: [
          "机构名称、国家、城市和官方网站。",
          "可合作方向：训练、比赛、营地、教练、裁判或互访。",
          "联系人公开状态、沟通记录和下一步动作。",
          "合作关系状态、核验状态和信息更新时间。",
          "是否适合家庭会员、俱乐部会员或国际训练会员。"
        ]
      }
    ]
  }
];

const membershipLandingCards: BusinessLandingCard[] = [
  {
    title: "家庭会员",
    summary: "适合青少年家庭，获取训练路径、俱乐部筛选、赛事提醒、政策资料和咨询优先级。",
    path: "/membership/family"
  },
  {
    title: "俱乐部会员",
    summary: "适合国内俱乐部和训练机构，建立官方展示页，发布动态，并对接赛事、裁判和海外资源。",
    path: "/membership/club"
  },
  {
    title: "国际训练会员",
    summary: "适合准备海外外训、训练营或交流赛的家庭与俱乐部，查看核验后的海外资源和出境清单。",
    path: "/membership/international"
  }
];

const membershipDetailSections: BusinessDetailSection[] = [
  {
    path: "/membership/family",
    kicker: "付费会员",
    title: "家庭会员",
    summary: "家庭会员面向正在了解、准备进入或已经参与水球训练的青少年家庭，重点解决信息筛选、训练路径和俱乐部连接问题。",
    notice: "会员服务不承诺比赛成绩、入队、升学或海外录取结果；它提供的是资料库、路径建议和资源连接。",
    details: [
      {
        heading: "适合的家庭",
        items: [
          "孩子已经会游泳，家长想判断是否适合进入水球训练。",
          "孩子已经在俱乐部训练，但家长需要更清晰的训练和参赛路径。",
          "家庭希望了解国内俱乐部、青少年赛事、运动员等级和学校选择信息。",
          "未来有海外训练、训练营或交流比赛计划，需要提前准备材料和预算。"
        ]
      },
      {
        heading: "会员开放内容",
        items: [
          "国内外水球资讯深度整理和来源追踪。",
          "青少年训练路径、赛事清单和参赛材料提醒。",
          "俱乐部信息库、城市资源和咨询优先入口。",
          "政策与升学资料库、国际申请辅助材料说明。",
          "海外训练营、交流赛和出境准备清单。"
        ]
      }
    ]
  },
  {
    path: "/membership/club",
    kicker: "付费会员",
    title: "俱乐部会员",
    summary: "俱乐部会员面向国内水球俱乐部、训练机构和学校队伍，用于规范展示资源、发布动态并与国内外资源建立连接。",
    notice: "俱乐部会员不是授权或隶属关系。页面会明确关系状态，避免把展示、合作和隶属混为一谈。",
    details: [
      {
        heading: "适合的机构",
        items: [
          "已有训练场地、教练和稳定训练安排的水球俱乐部。",
          "希望展示场地、教练、课程、比赛队和招生活动的机构。",
          "希望参与国内俱乐部交流训练、赛事互访或联合活动的机构。",
          "希望对接海外俱乐部、训练营、裁判或教练资源的机构。"
        ]
      },
      {
        heading: "会员开放内容",
        items: [
          "俱乐部资料页、场地与教练展示模板。",
          "俱乐部动态发布、青训活动记录和赛事报名信息同步。",
          "国内城市俱乐部信息库和合作需求登记。",
          "海外训练、交流赛和教练资源的优先匹配。",
          "规则培训、裁判资源和赛事组织资料库。"
        ]
      }
    ]
  },
  {
    path: "/membership/international",
    kicker: "付费会员",
    title: "国际训练会员",
    summary: "国际训练会员面向准备海外训练、训练营、交流比赛的家庭和俱乐部，重点提供核验后的海外资源、行前准备和风险边界。",
    notice: "国际训练会员不承诺签证、录取、入队或赛事名额；所有项目仍以主办方、俱乐部和官方要求为准。",
    details: [
      {
        heading: "适合的需求",
        items: [
          "家庭希望了解欧洲、美国、澳大利亚等海外俱乐部训练体系。",
          "孩子已有训练基础，准备尝试短期外训、训练营或交流比赛。",
          "俱乐部希望组织海外交流训练、联合训练营或赛事互访。",
          "家长需要提前核算签证、监护、保险、费用和时间安排。"
        ]
      },
      {
        heading: "会员开放内容",
        items: [
          "海外训练营、交流赛机会库。",
          "项目来源、核验状态、年龄要求、费用和报名截止提醒。",
          "未成年人出行、监护、保险和安全清单。",
          "训练档案、参赛材料和海外沟通资料准备建议。",
          "国际裁判、教练和俱乐部资源的优先咨询。"
        ]
      }
    ]
  }
];

function App() {
  const [payload, setPayload] = useState<ScrapePayload | null>(null);
  const [videos, setVideos] = useState<DomesticVideo[]>([]);
  const [adminVideos, setAdminVideos] = useState<DomesticVideo[]>([]);
  const [videoStorage, setVideoStorage] = useState<VideoStorageInfo | null>(null);
  const [articles, setArticles] = useState<Article[]>([]);
  const [adminArticles, setAdminArticles] = useState<Article[]>([]);
  const [isArticlesLoading, setIsArticlesLoading] = useState(true);
  const [isAdminArticlesLoading, setIsAdminArticlesLoading] = useState(false);
  const [page, setPage] = useState<PageKey>(() => pageFromLocation());
  const [error, setError] = useState<string | null>(null);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [articleError, setArticleError] = useState<string | null>(null);

  async function loadVideos() {
    setVideoError(null);
    try {
      const response = await fetch("/api/video-library");
      if (!response.ok) throw new Error(`API ${response.status}`);
      const data = (await response.json()) as { videos?: DomesticVideo[]; storage?: VideoStorageInfo };
      setVideos(data.videos ?? []);
      setVideoStorage(data.storage ?? null);
    } catch (fetchError) {
      setVideoError(fetchError instanceof Error ? fetchError.message : "视频服务暂不可用");
    }
  }

  async function loadAdminVideos() {
    setVideoError(null);
    try {
      const response = await fetch("/api/video-library/admin");
      if (!response.ok) throw new Error(`API ${response.status}`);
      const data = (await response.json()) as { videos?: DomesticVideo[]; storage?: VideoStorageInfo };
      setAdminVideos(data.videos ?? []);
      setVideoStorage(data.storage ?? null);
    } catch (fetchError) {
      setVideoError(fetchError instanceof Error ? fetchError.message : "视频服务暂不可用");
    }
  }

  async function loadArticles() {
    setIsArticlesLoading(true);
    setArticleError(null);
    try {
      const response = await fetch("/api/articles");
      if (!response.ok) throw new Error(`API ${response.status}`);
      const data = (await response.json()) as { articles?: Article[] };
      setArticles(data.articles ?? []);
    } catch (fetchError) {
      setArticleError(fetchError instanceof Error ? fetchError.message : "新闻服务暂不可用");
    } finally {
      setIsArticlesLoading(false);
    }
  }

  async function loadAdminArticles() {
    setIsAdminArticlesLoading(true);
    setArticleError(null);
    try {
      const response = await fetch("/api/articles/admin");
      if (!response.ok) throw new Error(`API ${response.status}`);
      const data = (await response.json()) as { articles?: Article[] };
      setAdminArticles(data.articles ?? []);
    } catch (fetchError) {
      setArticleError(fetchError instanceof Error ? fetchError.message : "新闻服务暂不可用");
    } finally {
      setIsAdminArticlesLoading(false);
    }
  }

  useEffect(() => {
    async function loadFeed() {
      setError(null);
      try {
        const response = await fetch("/api/feed");
        if (!response.ok) throw new Error(`API ${response.status}`);
        setPayload(await response.json());
      } catch (fetchError) {
        setError(fetchError instanceof Error ? fetchError.message : "内容服务暂不可用");
      }
    }

    void loadFeed();
    void loadVideos();
    void loadArticles();
  }, []);

  useEffect(() => {
    const legacyPage = pageFromLegacyHash();
    if (legacyPage) {
      window.history.replaceState({ page: legacyPage }, "", pagePaths[legacyPage]);
      setPage(legacyPage);
    } else if (["/domestic", "/global"].includes(window.location.pathname.replace(/\/+$/, "") || "/")) {
      window.history.replaceState({ page: "home" }, "", pagePaths.home);
      setPage("home");
    } else {
      const canonicalPath = canonicalPathFromLocation();
      if (canonicalPath) {
        const canonicalPage = pageFromPath(canonicalPath);
        window.history.replaceState({ page: canonicalPage }, "", canonicalPath);
        setPage(canonicalPage);
      }
    }

    const syncPage = () => {
      const canonicalPath = canonicalPathFromLocation();
      if (canonicalPath) {
        const canonicalPage = pageFromPath(canonicalPath);
        window.history.replaceState({ page: canonicalPage }, "", canonicalPath);
        setPage(canonicalPage);
        return;
      }
      setPage(pageFromLocation());
    };
    window.addEventListener("popstate", syncPage);
    window.addEventListener("hashchange", syncPage);
    return () => {
      window.removeEventListener("popstate", syncPage);
      window.removeEventListener("hashchange", syncPage);
    };
  }, []);

  useEffect(() => {
    if (page === "video-admin") {
      void loadAdminVideos();
    }
    if (page === "article-admin") {
      void loadAdminArticles();
    }
  }, [page]);

  function navigate(nextPage: PageKey, targetPath?: string) {
    const target = targetPath ?? pagePaths[nextPage];
    if (!target) return;

    setPage(nextPage);
    window.history.pushState({ page: nextPage }, "", target);
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }

  const eventItems = useMemo(() => (payload?.items ?? []).filter(isEventOrSchedule), [payload?.items]);
  const activeVideoId = page === "video-player" ? videoIdFromLocation() : null;
  const activeVideo = activeVideoId ? videos.find((video) => video.id === activeVideoId) : undefined;
  const activeArticleSlug = page === "article" ? articleSlugFromLocation() : null;
  const activeArticle = activeArticleSlug ? articles.find((article) => article.slug === activeArticleSlug) : undefined;
  const activeContentCardId = page === "content-card" ? contentCardIdFromLocation() : null;
  const activeContentCard = activeContentCardId ? contentCards.find((card) => card.id === activeContentCardId) : undefined;
  const activeResourceId = page === "resource-detail" ? resourceIdFromLocation() : null;
  const activeResourceSection = activeResourceId
    ? academyContent.academySections.find((section) => section.id === activeResourceId)
    : undefined;
  const activeYouthSectionId = page === "youth-detail" ? youthSectionIdFromLocation() : null;
  const activeYouthSection = activeYouthSectionId
    ? academyContent.youthSections.find((section) => section.id === activeYouthSectionId)
    : undefined;
  const domesticItems = useMemo(() => eventItems.filter(isDomesticItem), [eventItems]);

  const domesticSchedules = domesticItems.filter((item) => item.category === "赛程");
  const domesticNotices = domesticItems.filter((item) => item.category === "通知通告");
  const scheduleItems = eventItems.filter((item) => item.category === "赛程");
  const domesticVideoTargets = domesticSchedules;
  const youthArticles = articles.filter(isYouthArticle);

  if (page === "member-login") return <MemberLoginPage />;

  return (
    <div className={`app-shell${page === "home" ? " is-home-page" : ""}`}>
      {page === "live-competitions" ? null : <TopLiveDrawer schedules={scheduleItems} />}
      <Header
        activePage={page}
        articles={articles}
        videos={videos}
        onNavigate={navigate}
      />

      <main>
        {page === "home" ? (
          <HomePage />
        ) : null}

        {page === "notices" ? (
          <NoticeDesk notices={domesticNotices} isLoading={!payload && !error} error={error} />
        ) : null}

        {page === "live-competitions" ? (
          <LiveCompetitionAllPage schedules={scheduleItems} onNavigate={navigate} />
        ) : null}

        {page === "news" ? (
          <ArticleIndexPage
            title="赛事资讯"
            description="世界水球编辑整理的全球水球赛事新闻。"
            articles={articles}
            isLoading={isArticlesLoading}
            error={articleError}
            contentColumnId="news"
          />
        ) : null}

        {page === "youth" ? (
          <YouthWaterPoloPage
            articles={youthArticles}
            isLoading={isArticlesLoading}
            error={articleError}
          />
        ) : null}

        {page === "youth-athletes" ? <YouthAthletesPage /> : null}

        {page === "youth-detail" ? (
          <YouthKnowledgeDetailPage section={activeYouthSection} />
        ) : null}

        {page === "calendar" ? (
          <CalendarDesk schedules={scheduleItems} isLoading={!payload && !error} error={error} />
        ) : null}

        {page === "resource-detail" ? (
          <ResourceDetailPage section={activeResourceSection} />
        ) : null}

        {page === "clubs" ? (
          <ClubsPage />
        ) : null}

        {page === "club-detail" ? (
          <TaiyuanXuefuClubPage />
        ) : null}

        {page === "policies" ? (
          <PoliciesPage />
        ) : null}

        {page === "international-training" ? (
          <InternationalTrainingPage />
        ) : null}

        {page === "consultation" ? (
          <ConsultationPage />
        ) : null}

        {page === "about" ? (
          <AboutAcademyPage />
        ) : null}

        {page === "membership" ? (
          <MembershipPage />
        ) : null}

        {page === "content-card" ? (
          <ContentCardDetailPage card={activeContentCard} />
        ) : null}

        {page === "video" ? (
          <LivePlaceholderPage />
        ) : null}

        {page === "event-videos" ? (
          <EventVideosPage
            videoError={videoError}
            onNavigate={navigate}
          />
        ) : null}

        {page === "domestic-videos" ? (
          <VideoCategoryPage category="domestic" videos={videosForCategory(videos, "domestic")} videoError={videoError} />
        ) : null}

        {page === "global-videos" ? (
          <VideoCategoryPage category="global" videos={videosForCategory(videos, "global")} videoError={videoError} />
        ) : null}

        {page === "europe-videos" ? (
          <VideoCategoryPage category="europe" videos={videosForCategory(videos, "europe")} videoError={videoError} />
        ) : null}

        {page === "video-player" ? (
          <VideoPlayerPage video={activeVideo} videoId={activeVideoId} videoError={videoError} isLoading={!videoError && !activeVideo && Boolean(activeVideoId)} />
        ) : null}

        {page === "article" ? (
          <ArticlePage article={activeArticle} slug={activeArticleSlug} articleError={articleError} isLoading={isArticlesLoading} />
        ) : null}

        {page === "video-admin" ? (
          <VideoAdminPage
            targets={domesticVideoTargets}
            videos={adminVideos}
            videoStorage={videoStorage}
            videoError={videoError}
            onVideosChange={async () => {
              await Promise.all([loadVideos(), loadAdminVideos()]);
            }}
          />
        ) : null}

        {page === "article-admin" ? (
          <ArticleAdminPage
            articles={adminArticles}
            articleError={articleError}
            isLoading={isAdminArticlesLoading}
            onArticlesChange={async () => {
              await Promise.all([loadArticles(), loadAdminArticles()]);
            }}
          />
        ) : null}
      </main>
    </div>
  );
}

function TopLiveDrawer({ schedules }: { schedules: FeedItem[] }) {
  const [isExpanded, setIsExpanded] = useState(true);
  const liveScrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isExpanded && liveScrollRef.current) {
      liveScrollRef.current.scrollLeft = 0;
    }
  }, [isExpanded]);

  const cards = liveCompetitionCardsFromSchedules(schedules);

  return (
    <section
      className={`top-live-drawer${isExpanded ? " is-expanded" : ""}`}
      aria-label="现场比赛"
    >
      <button
        className="top-live-handle"
        type="button"
        aria-expanded={isExpanded}
        onClick={() => setIsExpanded((current) => !current)}
      >
        <Trophy size={16} />
        <span>现场比赛</span>
      </button>
      <div className="top-live-content" ref={liveScrollRef}>
        <div className="top-live-inner">
          {cards.map((card, index) => (
            <a
              className="top-live-card"
              href={card.href}
              key={`${card.title}-${card.href}-${index}`}
            >
              <span className="top-live-title-row">
                <span className="top-live-mark" aria-hidden="true" />
                <strong>{card.title}</strong>
                <span className="top-live-dot">·</span>
                <span className="top-live-phase">{card.phase}</span>
                <span className="top-live-status">即将推出</span>
              </span>
              <span className="top-live-middle">
                <span className="top-live-time">
                  <b>{card.time}</b>
                  <small>你的时间</small>
                </span>
                <span className="top-live-start">
                  起始列表
                  <ArrowRight size={24} strokeWidth={2.2} />
                </span>
              </span>
              <span className="top-live-footer">
                <span>{card.eventTitle}</span>
                <time>{card.date}</time>
              </span>
            </a>
          ))}
        </div>
        <a className="top-live-view-all" href="/live-competitions">查看全部</a>
      </div>
    </section>
  );
}

function liveCompetitionCardsFromSchedules(schedules: FeedItem[]): LiveCompetitionCard[] {
  const liveCards = schedules.slice(0, 8).map((item, index) => ({
    title: item.sourceName || "水球赛事",
    phase: item.category || "赛程",
    eventTitle: item.title,
    time: liveCompetitionTimes[index % liveCompetitionTimes.length],
    date: item.publishedAt ? formatShortDate(item.publishedAt) : "赛程",
    href: item.url
  }));

  if (liveCards.length >= 6) return liveCards;

  return [
    ...liveCards,
    ...liveCompetitionFallbacks.map((item, index) => ({
      title: item.meta,
      phase: item.time,
      eventTitle: item.title,
      time: liveCompetitionTimes[(liveCards.length + index) % liveCompetitionTimes.length],
      date: item.location,
      href: item.href
    }))
  ].slice(0, 6);
}

function LiveCompetitionAllPage({
  schedules,
  onNavigate
}: {
  schedules: FeedItem[];
  onNavigate: NavigateHandler;
}) {
  const cards = liveCompetitionCardsFromSchedules(schedules);

  function handleCardClick(event: React.MouseEvent<HTMLAnchorElement>, href: string) {
    if (/^https?:\/\//i.test(href)) return;
    event.preventDefault();
    const targetPath = internalPathFromUrl(href);
    onNavigate(pageFromPath(targetPath), targetPath);
  }

  return (
    <section className="live-competition-page" aria-labelledby="live-competition-page-title">
      <div className="live-competition-page-head">
        <span>Live Competitions</span>
        <h2 id="live-competition-page-title">现场比赛</h2>
      </div>
      <div className="live-competition-list">
        {cards.map((card, index) => (
          <a
            className="top-live-card top-live-card-full"
            href={card.href}
            key={`${card.title}-${card.href}-${index}`}
            onClick={(event) => handleCardClick(event, card.href)}
          >
            <span className="top-live-title-row">
              <span className="top-live-mark" aria-hidden="true" />
              <strong>{card.title}</strong>
              <span className="top-live-dot">·</span>
              <span className="top-live-phase">{card.phase}</span>
              <span className="top-live-status">即将推出</span>
            </span>
            <span className="top-live-middle">
              <span className="top-live-time">
                <b>{card.time}</b>
                <small>你的时间</small>
              </span>
              <span className="top-live-start">
                起始列表
                <ArrowRight size={24} strokeWidth={2.2} />
              </span>
            </span>
            <span className="top-live-footer">
              <span>{card.eventTitle}</span>
              <time>{card.date}</time>
            </span>
          </a>
        ))}
      </div>
    </section>
  );
}

function Header({
  activePage,
  articles,
  videos,
  onNavigate
}: {
  activePage: PageKey;
  articles: Article[];
  videos: DomesticVideo[];
  onNavigate: NavigateHandler;
}) {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [openSubnavLabel, setOpenSubnavLabel] = useState<string | null>(null);
  const [isHeaderCompact, setIsHeaderCompact] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [brandCollapseOffset, setBrandCollapseOffset] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [memberUser, setMemberUser] = useState<SiteMemberUser | null>(null);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const [isAccountActionLoading, setIsAccountActionLoading] = useState(false);
  const [accountActionError, setAccountActionError] = useState("");
  const navRef = useRef<HTMLElement | null>(null);
  const accountMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    document.documentElement.lang = "zh-CN";
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetch("/api/member-auth/session", { credentials: "same-origin", signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error("会员状态读取失败。");
        return response.json() as Promise<{ authenticated: boolean; user: SiteMemberUser | null }>;
      })
      .then((result) => {
        setMemberUser(result.authenticated ? result.user : null);
      })
      .catch((error: Error) => {
        if (error.name !== "AbortError") setMemberUser(null);
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    setOpenSubnavLabel(null);
    setIsMobileMenuOpen(false);
    setIsAccountMenuOpen(false);
  }, [activePage]);

  useEffect(() => {
    let rafId = 0;

    function updateCompactState() {
      const scrollY = window.scrollY;
      const isNarrowDesktop = window.matchMedia("(min-width: 761px) and (max-width: 1380px)").matches;
      const utilityHeight = isNarrowDesktop ? 46 : 50;
      const brandHeight = isNarrowDesktop ? 156 : 176;
      const liveDrawer = document.querySelector<HTMLElement>(".top-live-drawer.is-expanded");
      const liveHeight = liveDrawer?.getBoundingClientRect().height ?? 0;
      const stickyStartY = liveHeight + utilityHeight;
      const hideDistance = Math.max(0, brandHeight - utilityHeight);
      const compactEnterY = stickyStartY + hideDistance;
      const compactExitY = compactEnterY - 4;
      const nextOffset = Math.min(Math.max(scrollY - stickyStartY, 0), hideDistance);

      setBrandCollapseOffset(nextOffset);

      setIsHeaderCompact((current) => {
        if (!current && scrollY >= compactEnterY) return true;
        if (current && scrollY < compactExitY) return false;
        return current;
      });
    }

    function scheduleCompactStateUpdate() {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        updateCompactState();
      });
    }

    updateCompactState();
    window.addEventListener("scroll", scheduleCompactStateUpdate, { passive: true });
    window.addEventListener("wheel", scheduleCompactStateUpdate, { passive: true });
    window.addEventListener("touchmove", scheduleCompactStateUpdate, { passive: true });
    window.addEventListener("resize", scheduleCompactStateUpdate);
    document.addEventListener("scroll", scheduleCompactStateUpdate, { passive: true, capture: true });
    const compactSyncTimer = window.setInterval(scheduleCompactStateUpdate, 140);

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      window.clearInterval(compactSyncTimer);
      window.removeEventListener("scroll", scheduleCompactStateUpdate);
      window.removeEventListener("wheel", scheduleCompactStateUpdate);
      window.removeEventListener("touchmove", scheduleCompactStateUpdate);
      window.removeEventListener("resize", scheduleCompactStateUpdate);
      document.removeEventListener("scroll", scheduleCompactStateUpdate, true);
    };
  }, []);

  useEffect(() => {
    if (isHeaderCompact) {
      setOpenSubnavLabel(null);
    }
  }, [isHeaderCompact]);

  useEffect(() => {
    document.body.classList.toggle("nav-menu-lock", isMobileMenuOpen);
    return () => document.body.classList.remove("nav-menu-lock");
  }, [isMobileMenuOpen]);

  useEffect(() => {
    function handleDocumentPointerDown(event: PointerEvent) {
      if (!navRef.current?.contains(event.target as Node)) {
        setOpenSubnavLabel(null);
      }
      if (!accountMenuRef.current?.contains(event.target as Node)) {
        setIsAccountMenuOpen(false);
      }
    }

    function handleDocumentKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpenSubnavLabel(null);
        setIsAccountMenuOpen(false);
      }
    }

    document.addEventListener("pointerdown", handleDocumentPointerDown);
    document.addEventListener("keydown", handleDocumentKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handleDocumentPointerDown);
      document.removeEventListener("keydown", handleDocumentKeyDown);
    };
  }, []);

  async function logoutHeaderAccount() {
    setIsAccountActionLoading(true);
    setAccountActionError("");
    try {
      const response = await fetch("/api/member-auth/logout", { method: "POST", credentials: "same-origin" });
      if (!response.ok) throw new Error("退出登录失败，请稍后重试。");
      setMemberUser(null);
      setIsAccountMenuOpen(false);
      setIsMobileMenuOpen(false);
    } catch (error) {
      setAccountActionError(error instanceof Error ? error.message : "退出登录失败，请稍后重试。");
    } finally {
      setIsAccountActionLoading(false);
    }
  }

  async function deleteHeaderAccount() {
    const confirmed = window.confirm("注销账号后，会员资料、所有登录会话和个人战术库都会永久删除，且无法恢复。确定继续吗？");
    if (!confirmed) return;

    setIsAccountActionLoading(true);
    setAccountActionError("");
    try {
      const response = await fetch("/api/member-auth/account", {
        method: "DELETE",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ confirmation: "DELETE_ACCOUNT" })
      });
      const result = await response.json() as { deleted?: boolean; error?: string };
      if (!response.ok || !result.deleted) throw new Error(result.error || "注销账号失败，请稍后重试。");
      window.location.assign("/");
    } catch (error) {
      setAccountActionError(error instanceof Error ? error.message : "注销账号失败，请稍后重试。");
      setIsAccountActionLoading(false);
    }
  }

  const searchResults = useMemo(() => {
    const keyword = searchQuery.trim().toLowerCase();
    if (!keyword) return [];

    const articleResults = articles.map((article) => ({
      id: article.id,
      title: article.title,
      summary: article.summary,
      meta: `${article.category} · 本站新闻`,
      url: articlePath(article),
      external: false,
      haystack: `${article.title} ${article.summary} ${article.body} ${article.tags.join(" ")} ${article.source.sourceName}`.toLowerCase()
    }));

    const videoResults = videos.map((video) => {
      const categoryLabel = videoCategoryLabels[videoCategoryOf(video)];
      return {
        id: video.id,
        title: video.title,
        summary: video.description || video.eventTitle,
        meta: `${categoryLabel}视频 · ${videoSourceLabel(video)}`,
        url: videoWatchPath(video),
        external: false,
        haystack: `${categoryLabel} ${video.title} ${video.eventTitle} ${video.description ?? ""}`.toLowerCase()
      };
    });

    const staticResults = staticSearchEntries.map((entry) => ({
      id: entry.id,
      title: entry.title,
      summary: entry.summary,
      meta: entry.meta,
      url: entry.url,
      external: false,
      haystack: `${entry.title} ${entry.summary} ${entry.meta} ${entry.keywords.join(" ")}`.toLowerCase()
    }));

    return [...articleResults, ...videoResults, ...staticResults].filter((item) => item.haystack.includes(keyword)).slice(0, 10);
  }, [articles, searchQuery, videos]);

  function openSearch() {
    setIsSearchOpen(true);
    setOpenSubnavLabel(null);
    setIsMobileMenuOpen(false);
  }

  function handleMenuHref(event: React.MouseEvent<HTMLAnchorElement>, href: string) {
    if (href === "#") {
      event.preventDefault();
      return;
    }

    if (/^https?:\/\//i.test(href) || href === "/member/tactics") {
      setIsMobileMenuOpen(false);
      setOpenSubnavLabel(null);
      return;
    }

    event.preventDefault();
    const targetPath = internalPathFromUrl(href);
    setIsMobileMenuOpen(false);
    setOpenSubnavLabel(null);
    onNavigate(pageFromPath(targetPath), targetPath);
  }

  function renderNavLink(item: NavItem, className?: string, key?: string) {
    if (item.external) {
      return (
        <a className={className} href={item.path} key={key} target="_blank" rel="noreferrer" onClick={() => setOpenSubnavLabel(null)}>
          {item.label}
        </a>
      );
    }

    return (
      <a
        className={className}
        href={item.path}
        key={key}
        onClick={(event) => {
          setOpenSubnavLabel(null);
          if (item.key) handleNavClick(event, item.key, onNavigate);
        }}
      >
        {item.label}
      </a>
    );
  }

  function renderNavTrigger(item: NavItem, className: string | undefined, isOpen: boolean) {
    return (
      <button
        aria-expanded={isOpen}
        aria-haspopup="menu"
        className={className}
        onClick={() => {
          setOpenSubnavLabel((current) => current === item.label ? null : item.label);
          setIsSearchOpen(false);
        }}
        type="button"
      >
        {item.label}
      </button>
    );
  }

  function renderNavItem(item: NavItem) {
    const isActive = navItemIsActive(item, activePage);
    const className = isActive ? "active" : undefined;

    if (!item.children?.length) {
      return (
        <div className="nav-item" key={item.label}>
          {renderNavLink(item, className)}
        </div>
      );
    }

    const isOpen = openSubnavLabel === item.label;

    return (
      <div className={`nav-item has-subnav${isOpen ? " is-open" : ""}`} key={item.label}>
        {renderNavTrigger(item, className, isOpen)}
        <div className="subnav" role="menu" aria-label={`${item.label}二级导航`} hidden={!isOpen}>
          {item.children.map((child) => renderNavLink(child, navItemIsActive(child, activePage) ? "active" : undefined, child.label))}
        </div>
      </div>
    );
  }

  const headerStyle = {
    "--brand-collapse-offset": `${brandCollapseOffset}px`,
    ...(isHeaderCompact
      ? {
          top: 0,
          gridTemplateRows: "0 var(--wa-compact-height)",
          minHeight: "var(--wa-compact-height)"
        }
      : {})
  } as CSSProperties;
  const brandStyle = isHeaderCompact
    ? ({
        width: "var(--wa-brand-compact-width)",
        height: "var(--wa-compact-height)",
        borderRadius: 0,
        background: "#ffffff",
        boxShadow: "none",
        transform: "translateY(0)"
      } as CSSProperties)
    : undefined;
  const compactCardStyle = isHeaderCompact
    ? ({
        gridTemplateColumns: "var(--wa-compact-emblem-size) auto",
        columnGap: "var(--wa-compact-logo-gap)",
        height: "var(--wa-compact-height)",
        opacity: 1,
        transform: "translateY(0)"
      } as CSSProperties)
    : undefined;
  const compactEmblemStyle = isHeaderCompact
    ? ({
        width: "var(--wa-compact-emblem-size)",
        height: "var(--wa-compact-emblem-size)",
        opacity: 1,
        transitionDelay: "140ms",
        transform: "translateY(0)"
      } as CSSProperties)
    : undefined;
  const compactWordmarkStyle = isHeaderCompact
    ? ({
        justifySelf: "start",
        opacity: 1,
        transitionDelay: "140ms",
        transform: "translateY(0)"
      } as CSSProperties)
    : undefined;
  const mainRowStyle = isHeaderCompact
    ? ({
        gridRow: 2,
        marginLeft: "var(--wa-main-compact-left)",
        padding: "0 var(--wa-safe-left) 0 calc(var(--wa-nav-compact-overlap) + var(--wa-compact-main-padding))",
        borderRadius: "999px 0 0 999px"
      } as CSSProperties)
    : undefined;

  return (
    <header className={`site-header${isHeaderCompact ? " is-compact" : ""}${isMobileMenuOpen ? " is-menu-open" : ""}`} style={headerStyle}>
      <a className="brand" href="/" aria-label="水球学院首页" style={brandStyle} onClick={(event) => handleNavClick(event, "home", onNavigate)}>
        <span className="brand-full-card" aria-hidden="true">
          <span className="brand-main">
            <img className="brand-emblem" src="/assets/water-polo-academy-emblem-2026.png" alt="" />
            <span className="brand-wordmarks">
              <img className="brand-cn-logo" src="/assets/water-polo-academy-wordmark-cn-2026-ink.png" alt="" />
            </span>
          </span>
          <span className="brand-tagline">
            <span className="brand-en-text">
              <span>Water</span>
              <span className="brand-en-accent">Polo</span>
              <span>Academy</span>
            </span>
          </span>
        </span>
        <span className="brand-compact-card" style={compactCardStyle} aria-hidden="true">
          <span className="brand-compact-layer brand-compact-layer-desktop">
            <img className="brand-emblem" src="/assets/water-polo-academy-emblem-2026.png" style={compactEmblemStyle} alt="" />
            <span className="brand-compact-wordmark" style={compactWordmarkStyle}>
              <span className="brand-compact-en">
                <span className="brand-compact-line brand-compact-line-main">
                  <span>Water</span>
                  <span className="brand-en-accent">Polo</span>
                </span>
                <span className="brand-compact-line brand-compact-line-sub">Academy</span>
              </span>
            </span>
          </span>
          <span className="brand-compact-layer brand-compact-layer-mobile">
            <img className="brand-emblem" src="/assets/water-polo-academy-emblem-2026.png" style={compactEmblemStyle} alt="" />
            <span className="brand-compact-wordmark" style={compactWordmarkStyle}>
              <span className="brand-compact-en">
                <span className="brand-compact-line brand-compact-line-main">
                  <span>Water</span>
                  <span className="brand-en-accent">Polo</span>
                </span>
                <span className="brand-compact-line brand-compact-line-sub">Academy</span>
              </span>
            </span>
          </span>
        </span>
      </a>
      <div className="site-utility-row" aria-label="顶部导航">
        <nav className="utility-nav" aria-label="辅助导航">
          {utilityNavItems.map((item) => (
            <a href={item.href} key={item.label}>{item.label}</a>
          ))}
        </nav>
        <nav className="social-nav" aria-label="社交媒体占位入口">
          {socialNavItems.map((item) => (
            <a
              aria-label={`${item.label}（占位）`}
              className="social-link"
              href={item.href}
              key={item.label}
              onClick={(event) => {
                if (item.href === "#") event.preventDefault();
              }}
              title={`${item.label}（占位）`}
            >
              {item.icon}
            </a>
          ))}
        </nav>
      </div>
      <div className="site-main-row" style={mainRowStyle}>
        <nav className="main-nav" aria-label="主导航" ref={navRef}>
          {navItems.map((item) => renderNavItem(item))}
          <div className="header-auth-actions" aria-label="会员账号">
            <a href="/member-login?mode=login" onClick={(event) => handleMenuHref(event, "/member-login?mode=login")}>登录</a>
            <span aria-hidden="true" />
            <a href="/member-login?mode=register" onClick={(event) => handleMenuHref(event, "/member-login?mode=register")}>注册</a>
          </div>
        </nav>
        {memberUser ? (
          <div className="header-member-dock">
            <div className="header-member-account" ref={accountMenuRef}>
              <button
                className="header-member-trigger"
                type="button"
                aria-expanded={isAccountMenuOpen}
                aria-haspopup="menu"
                onClick={() => {
                  setAccountActionError("");
                  setIsAccountMenuOpen((current) => !current);
                }}
                title={memberUser.email || memberUser.account}
              >
                <UserRound size={16} />
                <span>{memberUser.displayName}</span>
              </button>
              {isAccountMenuOpen ? (
                <div className="header-member-menu" role="menu" aria-label="会员账号操作">
                  <div className="header-member-summary">
                    <strong>{memberUser.displayName}</strong>
                    <small>{memberUser.email || memberUser.account}</small>
                  </div>
                  {accountActionError ? <p role="alert">{accountActionError}</p> : null}
                  <button type="button" role="menuitem" disabled={isAccountActionLoading} onClick={() => void logoutHeaderAccount()}>
                    <LogOut size={16} />
                    退出登录
                  </button>
                  <button className="is-danger" type="button" role="menuitem" disabled={isAccountActionLoading} onClick={() => void deleteHeaderAccount()}>
                    <Trash2 size={16} />
                    注销账号
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
      <button
        className="mobile-menu-button"
        type="button"
        aria-label={isMobileMenuOpen ? "关闭导航菜单" : "打开导航菜单"}
        aria-expanded={isMobileMenuOpen}
        onClick={() => {
          setIsMobileMenuOpen((current) => !current);
          setOpenSubnavLabel(null);
          setIsSearchOpen(false);
        }}
      >
        <span />
        <span />
      </button>

      {isMobileMenuOpen ? (
        <div className="nav-menu-overlay" role="dialog" aria-modal="true" aria-label="站点导航菜单">
          <div className="nav-menu-head">
            <a href="/" className="nav-menu-brand" onClick={(event) => handleMenuHref(event, "/")}>
              <img src="/assets/water-polo-academy-emblem-2026.png" alt="" />
              <span className="nav-menu-brand-wordmark">
                <span>Water</span>
                <span className="brand-en-accent">Polo</span>
                <span>Academy</span>
              </span>
            </a>
            <button type="button" aria-label="关闭导航菜单" onClick={() => setIsMobileMenuOpen(false)}>
              <X size={28} />
            </button>
          </div>
          <nav className="nav-menu-primary" aria-label="主导航展开菜单">
            {navItems.map((item) => (
              <div className="nav-menu-item-group" key={item.label}>
                <a href={item.path} onClick={(event) => handleMenuHref(event, item.path)}>
                  {item.label}
                </a>
                {item.children?.map((child) => (
                  <a
                    className="nav-menu-subnav-link"
                    href={child.path}
                    key={child.label}
                    onClick={(event) => handleMenuHref(event, child.path)}
                  >
                    {child.label}
                  </a>
                ))}
              </div>
            ))}
          </nav>
          <div className="nav-menu-secondary">
            <nav className="nav-menu-utility" aria-label="辅助导航展开菜单">
              <a href="/member-login?mode=login" onClick={(event) => handleMenuHref(event, "/member-login?mode=login")}>
                登录
              </a>
              <a href="/member-login?mode=register" onClick={(event) => handleMenuHref(event, "/member-login?mode=register")}>
                注册
              </a>
              {utilityNavItems.map((item) => (
                <a href={item.href} key={item.label} onClick={(event) => handleMenuHref(event, item.href)}>
                  {item.label}
                </a>
              ))}
            </nav>
            {memberUser ? (
              <div className="nav-menu-member-account" aria-label="当前会员账号">
                <div>
                  <UserRound size={18} />
                  <span>
                    <strong>{memberUser.displayName}</strong>
                    <small>{memberUser.email || memberUser.account}</small>
                  </span>
                </div>
                {accountActionError ? <p role="alert">{accountActionError}</p> : null}
                <span className="nav-menu-member-actions">
                  <button type="button" disabled={isAccountActionLoading} onClick={() => void logoutHeaderAccount()}>
                    <LogOut size={16} />
                    退出登录
                  </button>
                  <button className="is-danger" type="button" disabled={isAccountActionLoading} onClick={() => void deleteHeaderAccount()}>
                    <Trash2 size={16} />
                    注销账号
                  </button>
                </span>
              </div>
            ) : null}
          </div>
          <nav className="nav-menu-social" aria-label="社交媒体展开菜单">
            {socialNavItems.map((item) => (
              <a
                href={item.href}
                key={item.label}
                aria-label={`${item.label}（占位）`}
                onClick={(event) => handleMenuHref(event, item.href)}
              >
                {item.icon}
              </a>
            ))}
          </nav>
        </div>
      ) : null}

      {isSearchOpen ? (
        <div className="modal-backdrop" role="presentation">
          <section className="utility-modal search-modal" role="dialog" aria-modal="true" aria-label={searchCopy.searchTitle}>
            <div className="modal-head">
              <h2>{searchCopy.searchTitle}</h2>
              <button className="modal-close" type="button" aria-label="关闭" onClick={() => setIsSearchOpen(false)}>
                <X size={19} />
              </button>
            </div>
            <label className="search-box">
              <Search size={19} />
              <input
                autoFocus
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder={searchCopy.searchPlaceholder}
              />
            </label>
            <div className="search-results">
              {searchQuery.trim() ? (
                searchResults.length ? (
                  searchResults.map((item) => (
                    <a
                      href={item.url}
                      target={item.external ? "_blank" : undefined}
                      rel={item.external ? "noreferrer" : undefined}
                      className="search-result"
                      key={item.id}
                      onClick={(event) => {
                        if (item.external) return;
                        event.preventDefault();
                        const targetPath = internalPathFromUrl(item.url);
                        setIsSearchOpen(false);
                        setSearchQuery("");
                        onNavigate(pageFromPath(targetPath), targetPath);
                      }}
                    >
                      <strong>{item.title}</strong>
                      <span>{item.summary}</span>
                      <small>{item.meta}</small>
                    </a>
                  ))
                ) : (
                  <div className="utility-empty">没有找到匹配内容。</div>
                )
              ) : (
                <div className="utility-empty">{searchCopy.searchEmpty}</div>
              )}
            </div>
          </section>
        </div>
      ) : null}
    </header>
  );
}

function HomePage() {
  return (
    <div className="home-visual-stage">
      <section className="home-hero-band">
        <Hero />
      </section>
      <InformationSourceLogoStrip />
    </div>
  );
}

function HomeCoreShowcase() {
  const latestNewsCards = contentCardsForColumn("news", 4);

  return (
    <section className="home-index" aria-label="首页内容索引">
      <HomeIndexSection
        id="news"
        kicker="News"
        title="News"
        description="从官方协会、赛事主办方和权威公开来源整理。"
        href="/news"
      >
        <HomeContentCardGrid cards={latestNewsCards} />
      </HomeIndexSection>

      <HomeIndexSection
        id="videos"
        kicker="Videos"
        title="Videos"
        description="按国内赛事、全球赛事和欧洲俱乐部赛事进入视频库。"
        href="/videos"
      >
        <div className="home-video-grid">
          {videoCards.map((video) => (
            <a className="home-video-card" href={video.url} key={video.category}>
              <span>{video.source}</span>
              <strong>{video.title}</strong>
              <p>{video.summary}</p>
              <small>
                进入视频
                <PlayCircle size={15} />
              </small>
            </a>
          ))}
        </div>
      </HomeIndexSection>
    </section>
  );
}

function HomeLiveCompetitions() {
  return (
    <section className="home-live-strip" id="competitions" aria-label="Live Competitions">
      <button type="button">Live Competitions</button>
      <a href="/news">最新赛事</a>
      <a href="/calendar">赛事日历</a>
      <a href="/videos">赛事视频</a>
    </section>
  );
}

function HomeHighlightSection() {
  const highlightCard = contentCardsForColumn("international-training", 1)[0] ?? contentCardsForColumn("news", 1)[0];
  const thumbnail = highlightCard ? contentCardThumbnail(highlightCard) : null;

  return (
    <section className="home-highlight-section" id="rankings" aria-label="首页重点内容">
      <div className="home-highlight-copy">
        <span>Highlight</span>
        <h2>水球成长路径重点内容</h2>
        <p>
          参考国际水球资讯、青少年训练路径、俱乐部资源和国内外训练营信息，帮助家庭判断下一步应该看什么、问什么、准备什么。
        </p>
        <div className="home-highlight-actions">
          <a className="jump-link" href="/member/tactics">
            打开水球攻防战术
            <ArrowRight size={16} />
          </a>
          <a className="jump-link secondary" href={highlightCard ? contentCardPath(highlightCard) : "/international-training/"}>
            查看专题
            <ArrowRight size={16} />
          </a>
        </div>
      </div>
      <figure className="home-highlight-media">
        <img src={thumbnail?.src ?? "/assets/waterpolo-hero.png"} alt={thumbnail?.alt ?? "水球比赛画面"} loading="lazy" />
      </figure>
    </section>
  );
}

function HomeGovernanceSection() {
  const items = [
    {
      id: "athletes",
      kicker: "Athletes",
      title: "运动员",
      summary: "后续用于整理青少年球员成长档案、国内外高水平运动员案例和可公开引用的训练路径资料。",
      href: "/youth"
    },
    {
      id: "committees",
      kicker: "Committees",
      title: "委员会",
      summary: "后续用于整理协会、赛事组织、技术委员会和俱乐部合作网络等公开信息。",
      href: "/about"
    },
    {
      id: "officials",
      kicker: "Officials",
      title: "裁判",
      summary: "后续用于展示裁判资源、规则解释、执裁路径和国际裁判相关公开资料。",
      href: "/resources/rules"
    }
  ];

  return (
    <section className="home-governance-section" aria-label="运动员、委员会与裁判">
      {items.map((item) => (
        <a className="home-governance-card" href={item.href} id={item.id} key={item.id}>
          <span>{item.kicker}</span>
          <strong>{item.title}</strong>
          <p>{item.summary}</p>
          <small>
            查看
            <ArrowRight size={14} />
          </small>
        </a>
      ))}
    </section>
  );
}

function HomePhotoSection() {
  const photoCards = contentCards
    .filter((card) => Boolean(contentCardThumbnail(card)))
    .slice(0, 8);

  return (
    <HomeIndexSection
      id="photos"
      kicker="Photos"
      title="Latest Photos"
      description="从已核验内容中选取与赛事、训练和俱乐部信息相关的图片。"
      href="/news"
    >
      <div className="home-photo-grid">
        {photoCards.map((card) => {
          const thumbnail = contentCardThumbnail(card);
          if (!thumbnail) return null;

          return (
            <a className="home-photo-card" href={contentCardPath(card)} key={card.id}>
              <img src={thumbnail.src} alt={thumbnail.alt} loading="lazy" />
              <span>{card.homeTitle}</span>
            </a>
          );
        })}
      </div>
    </HomeIndexSection>
  );
}

function HomeIndexSection({
  id,
  kicker,
  title,
  description,
  href,
  children
}: {
  id: string;
  kicker: string;
  title: string;
  description: string;
  href: string;
  children: ReactNode;
}) {
  return (
    <section className="home-index-section" id={id}>
      <div className="home-index-head">
        <div>
          <span>{kicker}</span>
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
        <a className="jump-link" href={href}>
          查看全部
          <ArrowRight size={16} />
        </a>
      </div>
      {children}
    </section>
  );
}

function HomeContentCardGrid({ cards }: { cards: ContentCard[] }) {
  if (!cards.length) {
    return <div className="empty-state">暂无已核验内容。</div>;
  }

  return (
    <div className="business-card-grid home-content-card-grid">
      {cards.map((card) => {
        const thumbnail = contentCardThumbnail(card);

        return (
          <a className="business-card home-news-card" href={contentCardPath(card)} key={card.id}>
            {thumbnail ? (
              <figure className="home-news-card-media">
                <img src={thumbnail.src} alt={thumbnail.alt} loading="lazy" />
              </figure>
            ) : (
              <FileText size={21} />
            )}
            <strong>{card.homeTitle}</strong>
            <p>{card.summary}</p>
            <span>
              {card.sourceName}
              <ArrowRight size={14} />
            </span>
          </a>
        );
      })}
    </div>
  );
}

function contentCardThumbnail(card: ContentCard) {
  return card.sourceOriginal?.images?.[0];
}

function contentCardsForColumn(columnId: ContentColumnId, limit: number) {
  return contentCards.filter((card) => card.columnId === columnId).slice(0, limit);
}

function InformationSourceLogoStrip() {
  return (
    <section className="partner-logo-band" aria-label="信息来源网站标志">
      {informationSourceLogos.map((logo) => (
        <a
          className="partner-logo-item"
          href={logo.href}
          key={logo.name}
          aria-label={`打开${logo.name}`}
          data-tone={logo.tone}
          target="_blank"
          rel="noreferrer"
          title={logo.name}
        >
          <img src={logo.src} alt={logo.alt} loading="lazy" />
        </a>
      ))}
    </section>
  );
}

function Hero() {
  const [isVideoReady, setIsVideoReady] = useState(false);

  const handleVideoReady = (video: HTMLVideoElement) => {
    setIsVideoReady(true);
    if (video.paused) void video.play().catch(() => undefined);
  };

  return (
    <article className="hero-card">
      <img
        className="hero-poster"
        src="/assets/waterpolo-hero-youth.png"
        alt=""
        aria-hidden="true"
      />
      <video
        className={isVideoReady ? "is-ready" : ""}
        autoPlay
        loop
        muted
        playsInline
        poster="/assets/waterpolo-hero-youth.png"
        preload="metadata"
        aria-label="水球运动员比赛动态画面"
        onCanPlay={(event) => handleVideoReady(event.currentTarget)}
        onPlaying={() => setIsVideoReady(true)}
        onError={() => setIsVideoReady(false)}
      >
        <source src="/assets/waterpolo-hero-motion.mp4" type="video/mp4" />
      </video>
      <div className="hero-overlay" />
      <div className="hero-content">
        <h1>
          <span>让水球成为</span>
          <span>孩子的下一项优势</span>
        </h1>
      </div>
    </article>
  );
}

function HomeFeedSection({
  title,
  icon,
  items,
  emptyText,
  actionLabel,
  actionPage,
  actionInHeader = false,
  error,
  articleBySourceItemId,
  onNavigate
}: {
  title: string;
  icon: ReactNode;
  items: FeedItem[];
  emptyText: string;
  actionLabel: string;
  actionPage: PageKey;
  actionInHeader?: boolean;
  error?: string | null;
  articleBySourceItemId?: Map<string, Article>;
  onNavigate: NavigateHandler;
}) {
  const action = (
    <a
      className="jump-link"
      href={pathForPage(actionPage)}
      onClick={(event) => handleNavClick(event, actionPage, onNavigate)}
    >
      {actionLabel}
      <ArrowRight size={16} />
    </a>
  );

  return (
    <section className="domestic-section home-feed-section">
      <div className="section-heading">
        <div>
          <h2>{title}</h2>
        </div>
        {actionInHeader ? action : <span className="domestic-count">{items.length} 条</span>}
      </div>

      {error ? <div className="inline-notice">内容服务暂不可用：{error}</div> : null}

      <div className="home-feed-list">
        {items.length ? (
          items.map((item, index) => (
            <HomePreviewRow
              item={item}
              icon={icon}
              article={articleBySourceItemId?.get(item.id)}
              requireArticleLink={Boolean(articleBySourceItemId)}
              action={index === 3 && !actionInHeader ? action : undefined}
              key={item.id}
            />
          ))
        ) : (
          <div className="empty-state">{emptyText}</div>
        )}
      </div>
    </section>
  );
}

function HomePreviewRow({
  item,
  icon,
  article,
  requireArticleLink,
  action
}: {
  item: FeedItem;
  icon: ReactNode;
  article?: Article;
  requireArticleLink?: boolean;
  action?: ReactNode;
}) {
  const content = (
    <>
      <time>{item.publishedAt ? formatShortDate(item.publishedAt) : "新收录"}</time>
      <span className="feed-category">{item.category}</span>
      <span className="home-preview-icon">{icon}</span>
      <strong>{article?.title ?? item.title}</strong>
      <span className="feed-source">{article ? "本站新闻" : requireArticleLink ? "待发布" : item.sourceName}</span>
      {article ? <ArrowRight size={16} /> : requireArticleLink ? <span className="feed-pending-dot" aria-hidden="true" /> : <ExternalLink size={16} />}
    </>
  );

  return (
    <article className="home-preview-row">
      {article ? (
        <a className="home-preview-link" href={articlePath(article)}>
          {content}
        </a>
      ) : requireArticleLink ? (
        <div className="home-preview-link is-disabled" aria-label={`${item.title}尚未发布`}>
          {content}
        </div>
      ) : (
        <a className="home-preview-link" href={item.url} target="_blank" rel="noreferrer">
          {content}
        </a>
      )}
      {action ? <div className="home-preview-action">{action}</div> : null}
    </article>
  );
}

function PublishedArticleCards({
  articles,
  isLoading,
  error
}: {
  articles: Article[];
  isLoading: boolean;
  error: string | null;
}) {
  if (error) {
    return (
      <section className="published-article-section" aria-label="已发布赛事新闻">
        <div className="inline-notice">新闻服务暂不可用：{error}</div>
      </section>
    );
  }

  if (isLoading) {
    return (
      <section className="published-article-section" aria-label="已发布赛事新闻">
        <div className="empty-state">正在加载已发布新闻...</div>
      </section>
    );
  }

  return (
    <section className="published-article-section" aria-label="已发布赛事新闻">
      {articles.length ? (
        <div className="published-article-grid">
          {articles.map((article) => (
            <a className="published-article-card" href={articlePath(article)} key={article.id}>
              <div className="published-article-media">
                {article.heroImageUrl ? (
                  <img src={article.heroImageUrl} alt={article.title} loading="lazy" />
                ) : (
                  <div className="published-article-placeholder" aria-hidden="true">
                    <ImagePlus size={30} />
                  </div>
                )}
              </div>
              <div className="published-article-body">
                <div className="published-article-meta">
                  <span>{article.category}</span>
                  <time>{formatShortDate(article.publishedAt ?? article.updatedAt)}</time>
                </div>
                <h3>{article.title}</h3>
                <p>{article.summary}</p>
                <div className="published-article-source">
                  <span>{article.source.sourceName}</span>
                  <ArrowRight size={16} />
                </div>
              </div>
            </a>
          ))}
        </div>
      ) : (
        <div className="empty-state">暂无已发布新闻。内容核验发布后，将在这里以图片和摘要形式展示。</div>
      )}
    </section>
  );
}

function ArticleIndexPage({
  title,
  description,
  articles,
  isLoading,
  error,
  contentColumnId
}: {
  title: string;
  description: string;
  articles: Article[];
  isLoading: boolean;
  error: string | null;
  contentColumnId?: ContentColumnId;
}) {
  return (
    <section className="article-index-page page-card">
      <div className="desk-header">
        <div>
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
        <span className="domestic-count">{isLoading ? "加载中" : `${articles.length} 篇`}</span>
      </div>

      <PublishedArticleCards articles={articles} isLoading={isLoading} error={error} />
      {contentColumnId ? (
        <ContentCardLibrarySection
          columnId={contentColumnId}
          title="真实赛事内容精选"
          description="从官方协会、赛事主办方和权威公开来源整理，进入详情后查看事实要点和来源链接。"
        />
      ) : null}
    </section>
  );
}

function CalendarDesk({
  schedules,
  isLoading,
  error
}: {
  schedules: FeedItem[];
  isLoading: boolean;
  error: string | null;
}) {
  return (
    <section className="desk-card page-card">
      <div className="desk-header">
        <div>
          <h2>赛事日历</h2>
          <p>{isLoading ? "正在加载赛程" : `当前收录 ${schedules.length} 条赛程`}</p>
        </div>
      </div>

      {error ? <div className="inline-notice">内容服务暂不可用：{error}</div> : null}

      <ScheduleList items={schedules} />
    </section>
  );
}

function ResourcesPage() {
  return (
    <section className="resources-page page-card">
      <div className="desk-header">
        <div>
          <h2>水球知识</h2>
          <p>面向俱乐部、学校、教练、家长和青少年运动员的中文知识中心。</p>
        </div>
        <span className="domestic-count">{academyContent.academySections.length} 个板块</span>
      </div>

      <div className="academy-overview-grid">
        {academyContent.academySections.map((section) => (
          <a className="academy-overview-card content-entry-card" href={resourceSectionPath(section)} key={section.id}>
            <div className="content-entry-media">
              <img src={section.image} alt={section.imageAlt} loading="lazy" />
            </div>
            <div className="content-entry-body">
              <span>{section.eyebrow}</span>
              <strong>{section.title}</strong>
              <p>{section.summary}</p>
              <small>
                进入详情
                <ArrowRight size={14} />
              </small>
            </div>
          </a>
        ))}
      </div>

      <ContentCardLibrarySection
        columnId="knowledge"
        title="水球知识重点参考"
        description="优先收录官方规则、入门训练和安全资料，方便家长判断孩子应该先学什么。"
      />
    </section>
  );
}

function ContentCardLibrarySection({
  columnId,
  title,
  description,
  limit = 6
}: {
  columnId: ContentColumnId;
  title: string;
  description: string;
  limit?: number;
}) {
  const cards = contentCards.filter((card) => card.columnId === columnId).slice(0, limit);
  if (!cards.length) return null;

  return (
    <section className="business-section" aria-label={title}>
      <div className="section-heading">
        <div>
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
      </div>
      <div className="business-card-grid">
        {cards.map((card) => (
          <a className="business-card" href={contentCardPath(card)} key={card.id}>
            <FileText size={21} />
            <strong>{card.homeTitle}</strong>
            <p>{card.summary}</p>
            <span>
              查看详情
              <ArrowRight size={14} />
            </span>
          </a>
        ))}
      </div>
    </section>
  );
}

function ContentCardDetailValue({ value }: { value: string | string[] }) {
  if (!Array.isArray(value)) return <span>{value}</span>;

  return (
    <div className="content-card-detail-paragraphs">
      {value.map((paragraph, index) => (
        <p key={`${paragraph}-${index}`}>{paragraph}</p>
      ))}
    </div>
  );
}

function ContentCardNarrativeSection({ items }: { items: ContentCard["sourceDigest"] }) {
  if (!items?.length) return null;

  return (
    <div className="content-card-narrative">
      {items.map((item) => (
        <section key={item.label}>
          <h3>{item.label}</h3>
          <ContentCardDetailValue value={item.value} />
        </section>
      ))}
    </div>
  );
}

function renderInlineMarkdownLinks(text: string): ReactNode[] {
  const parts: ReactNode[] = [];
  const linkPattern = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = linkPattern.exec(text))) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(
      <a href={match[2]} target="_blank" rel="noreferrer" key={`${match[2]}-${match.index}`}>
        {match[1]}
      </a>
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}

function ContentCardArticleText({ sections }: { sections: ContentCard["sourceTranslation"] }) {
  if (!sections?.length) return null;

  return (
    <div className="content-card-article-text">
      {sections.map((section, index) => (
        <section key={`${section.heading ?? "section"}-${index}`}>
          {section.heading ? <h3>{section.heading}</h3> : null}
          {section.paragraphs.map((paragraph, paragraphIndex) => (
            <p key={`${paragraph}-${paragraphIndex}`}>{renderInlineMarkdownLinks(paragraph)}</p>
          ))}
        </section>
      ))}
    </div>
  );
}

function ContentCardSourceOriginalPanel({ card }: { card: ContentCard }) {
  if (!card.sourceOriginal) {
    return (
      <div className="relationship-note">
        {card.humanReviewRequired
          ? "该内容涉及政策、俱乐部关系或国际训练安排，正式咨询前会再次核验来源和适用条件。"
          : "该内容来自公开来源。"}
      </div>
    );
  }

  return (
    <div className="relationship-note source-original-panel">
      <p>该内容来自公开来源。</p>
      <div className="source-original-meta">
        <span>原文</span>
        <strong>{card.sourceOriginal.title}</strong>
        {card.sourceOriginal.publishedAt ? <small>发布时间：{card.sourceOriginal.publishedAt}</small> : null}
        <a href={card.sourceUrl} target="_blank" rel="noreferrer">
          查看 World Aquatics 原文
          <ExternalLink size={14} />
        </a>
      </div>
      <p>{card.sourceOriginal.note}</p>
      <div className="source-original-image-grid">
        {card.sourceOriginal.images.map((image) => (
          <figure key={image.src}>
            <img src={image.src} alt={image.alt} loading="lazy" />
            <figcaption>{image.caption}</figcaption>
          </figure>
        ))}
      </div>
    </div>
  );
}

function ContentCardDetailPage({ card }: { card?: ContentCard }) {
  if (!card) {
    return <ContentMissingPage title="没有找到这条内容" backHref="/" backLabel="返回首页" />;
  }

  return (
    <article className="business-page page-card">
      <a className="ghost-control" href={contentCardBackHref(card)}>
        返回{contentColumnLabel(card.columnId)}
      </a>
      <div className="club-detail-hero">
        <div>
          <span className="business-kicker">{contentColumnLabel(card.columnId)}</span>
          <h2>{card.title}</h2>
          <p>{card.summary}</p>
        </div>
        <ContentCardSourceOriginalPanel card={card} />
      </div>

      {!card.sourceOriginal ? (
        <div className="detail-info-grid">
          {[
            ["来源", card.sourceName],
            ["核验日期", card.lastVerifiedAt],
            ["来源可信度", `${card.sourceCredibilityTier} / ${card.sourceCredibilityScore} 分`],
            ["核验状态", verificationStatusLabel(card.verificationStatus)],
            ["风险提示", riskLevelLabel(card.riskLevel)],
            ["地区", card.region]
          ].map(([label, value]) => (
            <div className="detail-info-card" key={label}>
              <span>{label}</span>
              <strong>{value}</strong>
            </div>
          ))}
        </div>
      ) : null}

      {card.sourceTranslation?.length ? (
        <div className="business-section">
          <ContentCardArticleText sections={card.sourceTranslation} />
        </div>
      ) : null}

      {card.sourceDigest?.length ? (
        <div className="business-section">
          <div className="section-heading">
            <div>
              <h2>原文解读</h2>
            </div>
          </div>
          <div className="content-card-analysis">
            <ContentCardNarrativeSection items={card.sourceDigest} />
          </div>
        </div>
      ) : null}

      <div className="business-section">
        <div className="section-heading">
          <div>
            <h2>关键信息</h2>
          </div>
        </div>
        <ol className="business-check-list">
          <li>
            <strong>判断：</strong>
            <ContentCardDetailValue value={card.customerValue} />
          </li>
          <li>
            <strong>下一步怎么做：</strong>
            <ContentCardDetailValue value={card.customerNextStep} />
          </li>
          {card.details.map((detail) => (
            <li key={detail.label}>
              <strong>{detail.label}：</strong>
              <ContentCardDetailValue value={detail.value} />
            </li>
          ))}
        </ol>
      </div>

      {card.humanReviewPoints.length ? (
        <div className="policy-alert">
          <ShieldCheck size={20} />
          <p>{card.sourceOriginal ? "请点击“查看原始来源”核对原始信息是否更新。" : `咨询前核验重点：${card.humanReviewPoints.join("；")}`}</p>
        </div>
      ) : null}

      <div className="business-actions">
        <a className="button primary" href={card.sourceUrl} target="_blank" rel="noreferrer">
          查看原始来源
          <ExternalLink size={16} />
        </a>
        <a className="button light" href="/consultation">
          联系我们
          <MessageCircle size={16} />
        </a>
      </div>
    </article>
  );
}

function ResourceDetailPage({ section }: { section?: AcademySection }) {
  if (!section) {
    return <ContentMissingPage title="没有找到这个水球知识板块" backHref="/resources/rules" backLabel="返回水球规则" />;
  }

  if (section.id === "rules") {
    return <WaterPoloRulesPage section={section} />;
  }

  return (
    <article className="content-detail-page page-card">
      <ContentDetailHeader
        eyebrow={section.eyebrow}
        title={section.title}
        summary={section.summary}
        image={section.image}
        imageAlt={section.imageAlt}
        imageSourceName={section.imageSourceName}
        imageSourceUrl={section.imageSourceUrl}
        backHref="/resources/rules"
        backLabel="返回水球规则"
      />

      <div className="academy-highlight-list" aria-label={`${section.title}重点`}>
        {section.highlights.map((highlight) => (
          <span key={highlight}>{highlight}</span>
        ))}
      </div>

      <div className="content-detail-grid">
        <div className="content-main-copy">
          {section.body.map((block) => (
            <section className="academy-copy-block" key={block.heading}>
              <h3>{block.heading}</h3>
              {block.paragraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </section>
          ))}

          {section.detailGroups.map((group) => (
            <section className="content-detail-group" key={group.heading}>
              <h3>{group.heading}</h3>
              <ol>
                {group.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ol>
            </section>
          ))}
        </div>

        <aside className="content-side-panel">
          <div className="academy-action-box">
            <strong>落地提示</strong>
            {section.actions.map((action) => (
              <p key={action}>
                <span aria-hidden="true">•</span>
                {action}
              </p>
            ))}
          </div>
          <SourceLinkList sources={section.sources} />
        </aside>
      </div>
    </article>
  );
}

const ruleReadingStructure = [
  {
    label: "原文完整呈现",
    value: `${waterPoloRulesMeta.clauseCount} 条`,
    note: "正文、附录、定义和教练挑战规程均按本地译本原文展示。"
  },
  {
    label: "规则逐条拆解",
    value: `${waterPoloRulesMeta.sectionCount} 个板块`,
    note: "每条规则下面增加判断逻辑、适用位置和执行后果说明。"
  },
  {
    label: "裁判手势图片",
    value: `${waterPoloRulesMeta.signalCount} 张`,
    note: "附录 5 的手势图全部进入页面，并配合哨声说明。"
  },
  {
    label: "哨声规则表",
    value: `${waterPoloWhistleRows.length} 类`,
    note: "把哨声时机、手势和依据单独列出，便于和条文互相对照。"
  }
];

const ruleSectionGuide: Record<string, { focus: string; scene: string; consequence: string }> = {
  s1: {
    focus: "先确认比赛空间、球门线、换人区和器材边界。",
    scene: "适用于赛前检查、场地布置、换人位置判断和比赛组织。",
    consequence: "场地或器材不符合要求时，比赛组织、换人和判罚都会失去统一基准。"
  },
  s2: {
    focus: "说明球队名单、上场人数、守门员身份、替补席纪律和换人路径。",
    scene: "适用于报名、首发、飞行换人、受伤替换、守门员替代等情形。",
    consequence: "人数、帽色或换人路径错误，可能造成违规入场、失去比赛资格或被判处罚。"
  },
  s3: {
    focus: "规定裁判员、助理裁判、计时员、记录员和 VAR 的职责边界。",
    scene: "适用于比赛控制、争议解释、事实判定和中止比赛。",
    consequence: "裁判对事实问题拥有最终控制权，比赛秩序以裁判员现场管理为准。"
  },
  s4: {
    focus: "规定实际比赛时间、节间休息、加时和控球计时的处理。",
    scene: "适用于停表、重启、进攻时间、最后时刻和必须分胜负的比赛。",
    consequence: "计时规则直接决定进攻是否超时、进球是否有效和比赛是否进入点球大战。"
  },
  s5: {
    focus: "规定暂停次数、请求条件、暂停时长和错误暂停的后果。",
    scene: "适用于控球方布置战术、替换队员、最后时刻调整和暂停违规。",
    consequence: "非法暂停可能直接送给对方罚点球，因此必须和控球权绑定理解。"
  },
  s6: {
    focus: "规定每节开球、进球后重新开始和不正确开始的处理。",
    scene: "适用于开局抢球、进球后站位、重新开始前队员位置和裁判鸣哨。",
    consequence: "错误开始会导致重新开始或判罚，影响第一时间的控球权。"
  },
  s7: {
    focus: "规定什么才算进球，以及哪些位置可以直接射门。",
    scene: "适用于门线争议、6 米外任意球、角球、罚点球和直接射门判断。",
    consequence: "进球成立必须看球整体是否完全越过球门线，而不是看水花或队员反应。"
  },
  s8: {
    focus: "列出普通犯规，核心是判给对方任意球但不罚出队员。",
    scene: "适用于控球、站底、双手触球、阻碍、推顶、2 米违例和进攻超时。",
    consequence: "普通犯规会改变球权和比赛节奏，但通常不形成短时间人数优势。"
  },
  s9: {
    focus: "列出排他犯规，核心是队员被罚出并形成短时间人数变化。",
    scene: "适用于抓、拉、按沉、回拽、干扰掷球、不当行为、暴力行为和不当再入场。",
    consequence: "排他犯规会造成多打少或更重处罚，是比赛局势变化最大的规则层。"
  },
  s10: {
    focus: "说明哪些犯规会被判点球，核心是保护可能进球的机会。",
    scene: "适用于 6 米区域、明显得分机会、守门员和防守方破坏进球的动作。",
    consequence: "点球直接给进攻方一次高概率射门，不能简单等同于普通身体接触。"
  },
  s11: {
    focus: "规定任意球在哪里发、如何发、什么时候可以直接射门。",
    scene: "适用于普通犯规、排他犯规后的重启，以及 6 米线内外的处理。",
    consequence: "任意球位置和是否已将球投入比赛，会决定后续射门是否合法。"
  },
  s12: {
    focus: "规定球门球的发生条件和主罚位置。",
    scene: "适用于进攻方最后触球出底线、射门未进出界和守门员重启。",
    consequence: "球门球会把控球权交给防守方，是一次完整的攻守转换。"
  },
  s13: {
    focus: "规定角球条件、角球位置和角球后的直接射门可能。",
    scene: "适用于防守方最后触球出底线、守门员扑救出界和 2 米线开球。",
    consequence: "角球通常给进攻方一次重新组织机会，和球门球方向正好相反。"
  },
  s14: {
    focus: "规定中立球的使用场景和重新开始方式。",
    scene: "适用于两队均未明确控球、同时犯规、裁判无法判定球权等情形。",
    consequence: "中立球的目标是让双方获得均等争球机会，而不是补偿某一方。"
  },
  s15: {
    focus: "规定罚点球的位置、队员站位、鸣哨和最后一分钟选择。",
    scene: "适用于点球主罚、守门员站位、其他队员退离和点球后的比赛处理。",
    consequence: "罚点球有严格程序，提前移动、干扰或位置错误都会影响结果。"
  },
  s16: {
    focus: "规定个人犯规累计、黄牌、红牌和球队官员处罚。",
    scene: "适用于队员累计犯规、教练席行为、持续违规和比赛纪律管理。",
    consequence: "个人犯规累计到上限会影响队员资格，黄牌红牌会影响球队管理。"
  },
  s17: {
    focus: "规定事故、受伤、流血和生病时比赛如何处理。",
    scene: "适用于队员出水、替换、医疗处理、流血停止和重新入场。",
    consequence: "安全处理优先，但是否能继续参赛、何时替换必须按规则执行。"
  },
  s18: {
    focus: "规定比赛设施、标识、球门、用球、泳帽和技术设备要求。",
    scene: "适用于场馆验收、比赛承办、器材准备和技术台配置。",
    consequence: "设施规则是竞赛公平和安全的基础，不只是场地装饰要求。"
  },
  s19: {
    focus: "规定沙滩水球的场地、人数、时间、犯规和处罚差异。",
    scene: "适用于沙滩水球项目，不应和标准水球规则直接混用。",
    consequence: "沙滩水球在人数、时间、犯规累计和场地上有独立规则体系。"
  },
  a3: {
    focus: "把规则中使用的专门术语逐一定义。",
    scene: "适用于阅读正文前统一概念，例如控球、排他犯规、明显得分机会、任意球。",
    consequence: "术语定义决定条文理解，很多判罚争议本质上是术语边界不清。"
  },
  a5: {
    focus: "说明官员职责、裁判配合、手势和号码示意。",
    scene: "适用于看懂裁判动作、记录台信号、VAR 协助和比赛控制流程。",
    consequence: "手势和官员职责不清，会导致看不懂判罚方向、犯规性质和队员号码。"
  },
  a6: {
    focus: "规定点球大战的官员、顺序、守门员更换和处罚。",
    scene: "适用于必须分出胜负且常规时间结束平局的比赛。",
    consequence: "点球大战是独立流程，主罚顺序、资格和守门员更换都不能随意调整。"
  },
  a7: {
    focus: "规定 VAR 可介入的范围、时机、流程和最终效力。",
    scene: "适用于进球争议、暴力行为、记录台错误、角球球门球等指定情形。",
    consequence: "VAR 不能无限回看，必须符合可回看类型和适当时刻。"
  },
  a10: {
    focus: "规定教练挑战的类型、时机、示意方式和成功失败后果。",
    scene: "适用于教练认为漏判、误判或指定 VAR 情形未回看的时候。",
    consequence: "挑战错误可能失去挑战权并产生黄牌或红牌后果。"
  }
};

function getRuleSectionAnchor(section: WaterPoloRuleSection) {
  return `rule-${section.id}`;
}

function isRuleSubheading(clause: WaterPoloRuleClause) {
  return clause.text.length <= 42 && !/[。；;]/.test(clause.text);
}

function getClauseBreakdown(section: WaterPoloRuleSection, clause: WaterPoloRuleClause) {
  const guide = ruleSectionGuide[section.id];
  const text = clause.text;
  const points: string[] = [];

  if (isRuleSubheading(clause)) {
    points.push(`这是《${section.title}》中的小节标题，用来标明后续条文讨论的具体主题。`);
    if (guide) {
      points.push(guide.focus);
      points.push(guide.scene);
    }
    return points;
  }

  if (guide) {
    points.push(guide.focus);
  }

  if (/不得|不能|禁止|无权/.test(text)) {
    points.push("这条规则含有明确禁止性要求，阅读时要先找出被禁止的动作、位置或时机。");
  } else if (/必须|须|应|只有|不得/.test(text)) {
    points.push("这条规则含有强制性要求，重点是确认谁必须做、在什么时间做、从哪个位置做。");
  } else if (/可|允许|有权/.test(text)) {
    points.push("这条规则给出允许或可选择的处理方式，重点是区分“可以做”和“必须做”。");
  } else {
    points.push("这条规则主要给出概念、程序或判断边界，需要和本章节其他条文一起理解。");
  }

  if (/罚点球|点球/.test(text)) {
    points.push("涉及点球时，要同时判断犯规位置、明显得分机会、主罚程序和守门员站位。");
  } else if (/排他|罚出|再入场|替补/.test(text)) {
    points.push("涉及罚出或再入场时，要同时看队员离场区域、再入场条件、替补是否合法。");
  } else if (/任意球|球门球|角球|中立球|掷球/.test(text)) {
    points.push("涉及掷球时，要同时确认球权方向、掷球位置、是否已经将球投入比赛。");
  } else if (/进球|球门线|射门/.test(text)) {
    points.push("涉及得分时，要回到球、球门线、射门位置和裁判确认四个判断点。");
  } else if (/时间|秒|分钟|暂停|控球/.test(text)) {
    points.push("涉及时间时，要区分实际比赛时间、自然流逝时间、控球时间和暂停时间。");
  } else if (/裁判|记录员|计时员|VAR|助理/.test(text)) {
    points.push("涉及官员时，要区分裁判员事实判定、记录台信号、计时职责和 VAR 协助范围。");
  } else if (guide) {
    points.push(guide.scene);
  }

  if (guide) {
    points.push(guide.consequence);
  }

  return Array.from(new Set(points)).slice(0, 4);
}

function WaterPoloRulesPage({ section }: { section: AcademySection }) {
  const sourceCount = section.sources.length;

  return (
    <article className="water-rules-page page-card">
      <a className="ghost-control" href="/">
        返回首页
      </a>

      <section className="water-rules-hero" aria-labelledby="water-rules-title">
        <div className="water-rules-hero-copy">
          <span>{section.eyebrow}</span>
          <h2 id="water-rules-title">水球规则</h2>
          <p>
            按 World Aquatics 水球规则结构整理的完整中文规则页。页面保留规则原文，并在每条规则后提供拆解讲解、适用场景和判罚重点。
          </p>
          <div className="water-rules-badges" aria-label="规则来源和适用说明">
            <span>中文译本：2025-06-25 生效版</span>
            <span>正文 §1-§19 + 附录 3 / 5 / 6 / 7 / 10</span>
            <span>完整裁判手势图示</span>
            <span>正式适用以官方英文规则为准</span>
          </div>
        </div>
        <div className="water-rules-hero-media">
          <img src="/assets/academy/source/rules-ball-goal.jpg" alt="水球门前区域和比赛用球" />
          <a className="image-credit" href={section.imageSourceUrl} target="_blank" rel="noreferrer">
            图片来源：{section.imageSourceName}
          </a>
        </div>
      </section>

      <section className="water-rules-metric-grid" aria-label="规则页内容结构">
        {ruleReadingStructure.map((metric) => (
          <div className="water-rules-metric-card" key={metric.label}>
            <span>{metric.label}</span>
            <strong>{metric.value}</strong>
            <p>{metric.note}</p>
          </div>
        ))}
      </section>

      <section className="water-rules-directory-panel">
        <div className="water-rules-section-heading">
          <span>Contents</span>
          <h3>规则目录</h3>
          <p>点击目录进入对应章节。每个章节均包含规则原文和拆解讲解，不以摘要替代条文。</p>
        </div>
        <div className="water-rules-directory-grid">
          {waterPoloRuleSections.map((ruleSection) => (
            <a className="water-rules-directory-card" href={`#${getRuleSectionAnchor(ruleSection)}`} key={ruleSection.id}>
              <span>{ruleSection.no}</span>
              <strong>{ruleSection.title}</strong>
              <small>{ruleSection.en}</small>
              <em>{ruleSection.clauses.length} 条</em>
            </a>
          ))}
        </div>
      </section>

      <section className="water-rules-doc-layout" aria-label="水球规则全文与拆解">
        <aside className="water-rules-doc-toc">
          <strong>章节索引</strong>
          <nav aria-label="水球规则章节索引">
            {waterPoloRuleSections.map((ruleSection) => (
              <a href={`#${getRuleSectionAnchor(ruleSection)}`} key={ruleSection.id}>
                <span>{ruleSection.no}</span>
                {ruleSection.title}
              </a>
            ))}
          </nav>
        </aside>

        <div className="water-rules-document">
          {waterPoloRuleSections.map((ruleSection) => (
            <section className="water-rules-doc-section" id={getRuleSectionAnchor(ruleSection)} key={ruleSection.id}>
              <header className="water-rules-doc-heading">
                <span>{ruleSection.no}</span>
                <div>
                  <h3>{ruleSection.title}</h3>
                  <p>{ruleSection.en}</p>
                </div>
                <small>{ruleSection.clauses.length} 条</small>
              </header>

              <div className="water-rules-full-list">
                {ruleSection.clauses.map((clause) => (
                  <article className="water-rules-full-clause" key={`${ruleSection.id}-${clause.number}`}>
                    <div className="water-rules-full-number">{clause.number}</div>
                    <div className="water-rules-full-body">
                      <div className="water-rules-original">
                        <span>规则原文</span>
                        <div className="water-rules-original-content" dangerouslySetInnerHTML={{ __html: clause.html }} />
                      </div>
                      <div className="water-rules-explain">
                        <span>拆解讲解</span>
                        <ul>
                          {getClauseBreakdown(ruleSection, clause).map((point) => (
                            <li key={point}>{point}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ))}
        </div>
      </section>

      <section className="water-rules-signal-section">
        <div className="water-rules-section-heading">
          <span>Appendix 5</span>
          <h3>裁判手势图示全集</h3>
          <p>以下 30 张手势图来自本地规则文件。页面完整展示手势编号、动作说明、类别和哨声配合。</p>
        </div>
        <div className="water-rules-principle" dangerouslySetInnerHTML={{ __html: waterPoloSignalPrincipleHtml }} />
        <div className="water-rules-signal-grid">
          {waterPoloSignalCards.map((signal) => (
            <figure className="water-rules-signal-card" key={signal.id}>
              <div>
                <img src={signal.image} alt={signal.alt} loading="lazy" />
              </div>
              <figcaption>
                <div className="water-rules-signal-meta">
                  <span>{signal.id}</span>
                  <small>{signal.category}</small>
                  <small>{signal.whistle}</small>
                </div>
                <strong>手势说明</strong>
                <p dangerouslySetInnerHTML={{ __html: signal.gestureHtml }} />
                <strong>哨声配合</strong>
                <p dangerouslySetInnerHTML={{ __html: signal.whistleHtml }} />
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="water-rules-whistle-section">
        <div className="water-rules-section-heading">
          <span>Whistle</span>
          <h3>哨声规则与手势配合</h3>
          <p>哨声负责让比赛停开和确认判罚，手势负责说明方向、犯规性质和号码。以下内容完整转入站内规则页。</p>
        </div>
        <div className="water-rules-whistle-notes">
          <div dangerouslySetInnerHTML={{ __html: waterPoloWhistleNoticeHtml }} />
          <div dangerouslySetInnerHTML={{ __html: waterPoloWhistlePrincipleHtml }} />
        </div>
        <div className="water-rules-whistle-table" role="table" aria-label="水球裁判哨声规则">
          <div role="row" className="water-rules-whistle-head">
            <span role="columnheader">情形</span>
            <span role="columnheader">哨声</span>
            <span role="columnheader">配合手势</span>
            <span role="columnheader">依据</span>
          </div>
          {waterPoloWhistleRows.map((row) => (
            <div role="row" className="water-rules-whistle-row" key={row.scene}>
              <span role="cell">{row.scene}</span>
              <span role="cell" dangerouslySetInnerHTML={{ __html: row.whistleHtml }} />
              <span role="cell">{row.signal}</span>
              <span role="cell">{row.basis}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="water-rules-source-panel">
        <div>
          <span>Source Check</span>
          <h3>规则来源与核验</h3>
          <p>{waterPoloRulesMeta.sourceNote}</p>
          <p>
            当前页面使用本地 HTML 文件生成：规则正文、定义术语、官员附录、点球大战、VAR、教练挑战、裁判手势和哨声表均已纳入。页面引用 {sourceCount} 个来源链接供后续核验。
          </p>
        </div>
        <SourceLinkList sources={section.sources} />
      </section>
    </article>
  );
}

function BusinessDetailPage({
  section,
  backHref,
  backLabel
}: {
  section: BusinessDetailSection;
  backHref: string;
  backLabel: string;
}) {
  return (
    <article className="business-page page-card">
      <a className="ghost-control" href={backHref}>
        {backLabel}
      </a>
      <div className="club-detail-hero">
        <div>
          <span className="business-kicker">{section.kicker}</span>
          <h2>{section.title}</h2>
          <p>{section.summary}</p>
        </div>
        <div className="relationship-note">{section.notice}</div>
      </div>

      {section.details.map((group) => (
        <div className="business-section" key={group.heading}>
          <div className="section-heading">
            <div>
              <h2>{group.heading}</h2>
            </div>
          </div>
          <ol className="business-check-list">
            {group.items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ol>
        </div>
      ))}
    </article>
  );
}

function ClubsPage() {
  const activeSection = businessSectionFromLocation(clubDirectorySections);
  if (activeSection) {
    return <BusinessDetailPage section={activeSection} backHref="/clubs" backLabel="返回俱乐部" />;
  }

  const upcomingCities = ["北京", "上海", "广州", "深圳", "成都", "杭州"];

  return (
    <section className="business-page page-card">
      <div className="desk-header business-header">
        <div>
          <h2>水球俱乐部</h2>
          <p>水球学院会陆续整理国内主要城市和国际俱乐部信息，每个俱乐部都必须明确关系状态和核验状态。</p>
        </div>
        <span className="domestic-count">栏目库驱动</span>
      </div>

      <div className="featured-club-panel">
        <div>
          <span className="business-kicker">入驻俱乐部</span>
          <h3>入驻俱乐部资料库</h3>
          <p>
            水球学院会为入驻俱乐部建立统一资料页，展示俱乐部简介、训练场地、教练、设施、课程、
            参赛情况和联系方式。每个俱乐部都是独立主体，页面必须标注关系状态和核验状态。
          </p>
        </div>
        <div className="featured-club-meta" aria-label="入驻俱乐部基础字段">
          <span>城市：俱乐部所在城市</span>
          <span>适合年龄：按俱乐部实际课程填写</span>
          <span>方向：启蒙 / 基础 / 提高 / 比赛队</span>
          <span>关系：入驻展示 / 合作 / 待核验</span>
        </div>
        <div className="business-actions">
          <a className="button primary" href="/clubs/taiyuan-xuefu">
            查看已入驻俱乐部
            <ArrowRight size={16} />
          </a>
          <a className="button light" href="/clubs/submit">
            提交俱乐部信息
            <FileText size={16} />
          </a>
        </div>
      </div>

      <div className="business-section">
        <div className="section-heading">
          <div>
            <h2>全国主要城市俱乐部</h2>
            <p>后续每个城市俱乐部将按栏目库字段补齐场地、教练、设施、年龄段、参赛情况和联系方式。</p>
          </div>
        </div>
        <div className="city-club-grid">
          {upcomingCities.map((city) => (
            <a className="city-club-card" href="/clubs/domestic" key={city}>
              <MapPin size={20} />
              <strong>{city}</strong>
              <span>待更新俱乐部信息</span>
            </a>
          ))}
        </div>
      </div>

      <ContentCardLibrarySection
        columnId="clubs"
        title="水球俱乐部线索库"
        description="先按公开来源收录俱乐部和学校队线索，帮助家长判断训练路径；未确认合作关系的机构不会写成合作或隶属关系。"
      />
    </section>
  );
}

function TaiyuanXuefuClubPage() {
  return (
    <article className="business-page page-card">
      <a className="ghost-control" href="/clubs">
        返回俱乐部
      </a>
      <div className="club-detail-hero">
        <div>
          <span className="business-kicker">入驻俱乐部</span>
          <h2>太原学府俱乐部</h2>
          <p>
            太原学府俱乐部是入驻水球学院网站的太原青少年水球俱乐部。水球学院负责展示、介绍和连接相关训练资源；
            俱乐部课程、场地、教练和费用以实际咨询确认为准。
          </p>
        </div>
        <div className="relationship-note">
          入驻展示不等于隶属关系。没有正式合作文件前，页面不使用“官方直属”“授权基地”等表述。
        </div>
      </div>

      <div className="detail-info-grid">
        {[
          ["城市", "山西太原"],
          ["适合年龄", "9-13 岁青少年"],
          ["训练方向", "兴趣启蒙、基础训练、提高训练、比赛队路径"],
          ["关系状态", "入驻水球学院网站的独立俱乐部"],
          ["信息状态", "基础页面已建立，场地、教练、设施和课程信息待补充"],
          ["联系方式", "微信（WeChat）和邮箱待配置"]
        ].map(([label, value]) => (
          <div className="detail-info-card" key={label}>
            <span>{label}</span>
            <strong>{value}</strong>
          </div>
        ))}
      </div>

      <div className="business-section">
        <div className="section-heading">
          <div>
            <h2>后续需要补充的信息</h2>
            <p>这些字段会进入俱乐部库，作为未来全国俱乐部统一展示模板。</p>
          </div>
        </div>
        <ol className="business-check-list">
          <li>俱乐部简介、训练理念和适合人群。</li>
          <li>场地名称、地址、公开照片和设施清单。</li>
          <li>教练姓名、照片、资质和简介。</li>
          <li>课程分层、训练时间和比赛队路径。</li>
          <li>微信号、微信二维码、联系邮箱和隐私说明。</li>
        </ol>
      </div>
    </article>
  );
}

function PoliciesPage() {
  const activeSection = businessSectionFromLocation(policyDetailSections);
  if (activeSection) {
    return <BusinessDetailPage section={activeSection} backHref="/policies" backLabel="返回政策与升学" />;
  }

  return (
    <section className="business-page page-card">
      <div className="desk-header business-header">
        <div>
          <h2>政策与升学</h2>
          <p>以公开政策为准，帮助家庭理解水球成长机会。平台只做公开信息整理和路径解释，不承诺升学、录取或奖学金结果。</p>
        </div>
        <span className="domestic-count">高风险栏目</span>
      </div>

      <div className="policy-alert">
        <ShieldCheck size={20} />
        <p>政策信息变化快，后续每篇内容都必须展示来源、发布日期和本站最后核验日期。</p>
      </div>

      <div className="business-card-grid">
        {policyLandingCards.map(({ title, summary, path }) => (
          <a className="business-card" href={path} key={title}>
            <GraduationCap size={21} />
            <strong>{title}</strong>
            <p>{summary}</p>
            <span>
              进入栏目
              <ArrowRight size={14} />
            </span>
          </a>
        ))}
      </div>

      <ContentCardLibrarySection
        columnId="policies"
        title="政策与升学重点参考"
        description="只收录官方或权威来源，所有内容都保留核验日期和风险提示，不承诺升学结果。"
      />
    </section>
  );
}

function InternationalTrainingPage() {
  const activeSection = businessSectionFromLocation(internationalDetailSections);
  if (activeSection) {
    return <BusinessDetailPage section={activeSection} backHref="/international-training/" backLabel="返回国际训练" />;
  }

  return (
    <section className="business-page page-card">
      <div className="desk-header business-header">
        <div>
          <h2>国内外训练营</h2>
          <p>连接海外训练营、交流比赛与出境准备资源，但不把海外训练包装成升学、签约或入选结果的确定性承诺。</p>
        </div>
        <span className="domestic-count">机会库</span>
      </div>

      <div className="international-path">
        {["了解项目", "核验来源", "评估孩子基础", "确认监护和保险", "形成训练档案"].map((step, index) => (
          <div key={step}>
            <span>{index + 1}</span>
            <strong>{step}</strong>
          </div>
        ))}
      </div>

      <div className="business-card-grid">
        <a className="business-card international-tactics-card" href="/member/tactics">
          <PlayCircle size={21} />
          <small className="business-kicker">会员教学工具</small>
          <strong>水球攻防战术</strong>
          <p>无需登录即可浏览和播放；设计或保存个人战术时再注册登录。</p>
          <span>
            进入攻防战术
            <ArrowRight size={14} />
          </span>
        </a>
        {internationalLandingCards.map(({ title, summary, path }) => (
          <a className="business-card" href={path} key={title}>
            <Plane size={21} />
            <strong>{title}</strong>
            <p>{summary}</p>
            <span>
              查看方向
              <ArrowRight size={14} />
            </span>
          </a>
        ))}
      </div>

      <ContentCardLibrarySection
        columnId="international-training"
        title="国际训练重点参考"
        description="只展示水球相关海外训练来源和出境准备线索，所有项目都需要核验费用、签证、监护和保险。"
      />
    </section>
  );
}

function ConsultationPage() {
  const [wechatCopied, setWechatCopied] = useState(false);
  const wechatAccount = contactChannels.wechat.account.trim();
  const wechatQrImage = contactChannels.wechat.qrImage.trim();
  const emailAddress = contactChannels.email.address.trim();

  async function copyWechatAccount() {
    if (!wechatAccount || !navigator.clipboard) return;
    await navigator.clipboard.writeText(wechatAccount);
    setWechatCopied(true);
    window.setTimeout(() => setWechatCopied(false), 1800);
  }

  return (
    <section className="business-page page-card contact-page">
      <header className="contact-hero">
        <div>
          <span className="contact-kicker">CONTACT WATER POLO ACADEMY</span>
          <h1>联系我们</h1>
          <p>训练、赛事、俱乐部和国内外训练营相关问题，可以通过微信（WeChat）或邮箱与水球学院联系。</p>
        </div>
        <div className="contact-response-note">
          <Clock3 size={20} />
          <div>
            <span>回复时间</span>
            <strong>{contactChannels.responseTime}</strong>
          </div>
        </div>
      </header>

      <div className="contact-page-body">
        <div className="contact-channel-grid">
          <article className="contact-channel-card contact-channel-card--wechat">
            <div className="contact-channel-heading">
              <span className="contact-channel-icon" aria-hidden="true">
                <MessageCircle size={24} />
              </span>
              <div>
                <span>即时沟通</span>
                <h2>微信（WeChat）</h2>
              </div>
            </div>

            <div className="contact-wechat-layout">
              <div className={`contact-qr-frame${wechatQrImage ? " has-image" : ""}`}>
                {wechatQrImage ? (
                  <img src={wechatQrImage} alt="水球学院微信二维码" />
                ) : (
                  <>
                    <MessageCircle size={34} />
                    <span>二维码待公布</span>
                  </>
                )}
              </div>
              <div className="contact-channel-details">
                <p>适合训练方向、俱乐部沟通和活动安排等需要及时确认的问题。</p>
                <div className="contact-value-block">
                  <span>微信号</span>
                  <strong>{wechatAccount || "待公布"}</strong>
                </div>
                <button className="button contact-action-button" type="button" disabled={!wechatAccount} onClick={() => void copyWechatAccount()}>
                  {wechatCopied ? <Check size={16} /> : <Copy size={16} />}
                  {wechatCopied ? "已复制" : wechatAccount ? "复制微信号" : "微信号待公布"}
                </button>
              </div>
            </div>
          </article>

          <article className="contact-channel-card contact-channel-card--email">
            <div className="contact-channel-heading">
              <span className="contact-channel-icon" aria-hidden="true">
                <Mail size={24} />
              </span>
              <div>
                <span>详细沟通</span>
                <h2>邮箱（Email）</h2>
              </div>
            </div>
            <div className="contact-channel-details contact-email-details">
              <p>适合合作方案、训练营资料、赛事信息和需要附带文件的正式沟通。</p>
              <div className="contact-value-block contact-email-value">
                <span>联系邮箱</span>
                <strong>{emailAddress || "待公布"}</strong>
              </div>
              {emailAddress ? (
                <a className="button contact-action-button" href={`mailto:${emailAddress}`}>
                  <Mail size={16} />
                  发送邮件
                </a>
              ) : (
                <button className="button contact-action-button" type="button" disabled>
                  <Mail size={16} />
                  联系邮箱待公布
                </button>
              )}
            </div>
          </article>
        </div>

        <div className="contact-privacy-band">
          <ShieldCheck size={20} />
          <div>
            <strong>隐私说明</strong>
            <p>涉及未成年人年龄、训练经历和联系方式的信息，仅用于沟通与训练路径判断，不公开展示。</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function AboutAcademyPage() {
  return (
    <article className="business-page page-card about-academy-page">
      <div className="club-detail-hero">
        <div>
          <span className="business-kicker">品牌定位</span>
          <h2>水球学院是什么</h2>
          <p>
            水球学院是面向中国家庭的水球信息与资源连接平台。它把全球水球资讯、水球知识、国内外俱乐部、
            政策与升学信息、海外训练资源整理成中文内容，帮助家长和孩子建立更清楚的训练与参赛路径。
          </p>
        </div>
        <div className="relationship-note">
          水球学院不是单一俱乐部。入驻网站的俱乐部都是独立主体，页面会区分入驻展示、合作关系和隶属关系。
        </div>
      </div>

      <div className="detail-info-grid">
        {[
          ["平台角色", "信息整理、路径咨询、资源连接和长期成长档案"],
          ["服务对象", "关注水球训练、比赛、俱乐部和海外机会的中国家庭"],
          ["资源方向", "赛事资讯、水球知识、俱乐部库、政策库、国际训练库"],
          ["合作边界", "公开展示、入驻展示、合作关系和隶属关系必须分开标注"],
          ["联系方式", "通过微信（WeChat）和邮箱联系水球学院"],
          ["会员机制", "深度资料、资源筛选和个性化咨询将通过付费会员开放"]
        ].map(([label, value]) => (
          <div className="detail-info-card" key={label}>
            <span>{label}</span>
            <strong>{value}</strong>
          </div>
        ))}
      </div>

      <div className="business-section">
        <div className="section-heading">
          <div>
            <h2>我们解决的问题</h2>
            <p>平台不会只解释标题，而是把家长真正需要判断的内容拆成可执行信息。</p>
          </div>
        </div>
        <ol className="business-check-list">
          <li>孩子是否适合进入水球训练，应该从游泳基础、年龄、身体条件和长期兴趣判断。</li>
          <li>本地训练如何开始，需要看场地、教练、课程分层、训练频率和比赛队路径。</li>
          <li>国内赛事如何参加，需要整理报名条件、年龄组、赛事等级、材料和时间节点。</li>
          <li>学校选择是否有帮助，需要基于公开政策、学校资源、孩子成绩和训练档案判断。</li>
          <li>海外训练是否值得去，需要比较俱乐部来源、训练质量、费用、签证、监护和保险。</li>
        </ol>
      </div>

      <div className="business-actions">
        <a className="button primary" href="/consultation">
          联系我们
          <ArrowRight size={16} />
        </a>
        <a className="button light" href="/membership">
          注册会员
          <Users size={16} />
        </a>
      </div>
    </article>
  );
}

function SiteMembershipAccess({ compact = false }: { compact?: boolean }) {
  const [mode, setMode] = useState<"register" | "login">(() => {
    return new URLSearchParams(window.location.search).get("mode") === "login" ? "login" : "register";
  });
  const [user, setUser] = useState<SiteMemberUser | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState("正在检查会员登录状态...");
  const [isLoading, setIsLoading] = useState(true);
  const returnPath = useMemo(() => {
    const requested = new URLSearchParams(window.location.search).get("return");
    return requested?.startsWith("/") && !requested.startsWith("//") ? requested : null;
  }, []);

  useEffect(() => {
    let active = true;
    fetch("/api/member-auth/session", { credentials: "same-origin" })
      .then(async (response) => {
        if (!response.ok) throw new Error("会员服务暂时不可用。");
        return response.json() as Promise<{ authenticated: boolean; user: SiteMemberUser | null }>;
      })
      .then((result) => {
        if (!active) return;
        if (result.authenticated && result.user) {
          if (returnPath) {
            window.location.replace(returnPath);
            return;
          }
          setUser(result.user);
          setStatus("已登录水球学院会员");
        } else {
          setStatus(mode === "register" ? "注册后即可进入水球学院会员板块" : "登录后继续使用会员板块");
        }
      })
      .catch((error: Error) => {
        if (active) setStatus(error.message || "会员服务暂时不可用。");
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [mode, returnPath]);

  async function submitMembership(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsLoading(true);
    setStatus(mode === "register" ? "正在注册水球学院会员..." : "正在登录水球学院...");
    try {
      const response = await fetch(`/api/member-auth/${mode}`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          account: mode === "login" ? identifier : undefined,
          email: mode === "register" ? identifier : undefined,
          password,
          displayName: mode === "register" ? displayName : undefined
        })
      });
      const result = await response.json() as { user?: SiteMemberUser; error?: string };
      if (!response.ok || !result.user) throw new Error(result.error || "会员账号操作失败。");
      setPassword("");
      if (returnPath) {
        window.location.assign(returnPath);
        return;
      }
      setUser(result.user);
      setStatus(mode === "register" ? "水球学院会员注册成功" : "已登录水球学院会员");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "会员账号操作失败。");
    } finally {
      setIsLoading(false);
    }
  }

  async function logoutMembership() {
    setIsLoading(true);
    try {
      await fetch("/api/member-auth/logout", { method: "POST", credentials: "same-origin" });
    } finally {
      setUser(null);
      setMode("login");
      setStatus("已退出水球学院会员账号");
      setIsLoading(false);
    }
  }

  if (compact && isLoading) {
    return <div className="member-login-loading" role="status">正在检查会员登录状态...</div>;
  }

  if (compact && user) {
    return (
      <div className="member-login-loading" role="status">
        <span>已登录水球学院会员</span>
        <a className="button primary" href={returnPath || "/membership"}>继续进入</a>
      </div>
    );
  }

  if (compact) {
    return (
      <section className="member-login-columns" aria-label="水球学院会员登录">
        <form className="membership-auth-form member-login-email" onSubmit={(event) => void submitMembership(event)}>
          <h2>{mode === "register" ? "邮箱注册" : "邮箱登录"}</h2>
          <div className="membership-auth-tabs" role="tablist" aria-label="邮箱会员账号操作">
            <button type="button" role="tab" aria-selected={mode === "register"} className={mode === "register" ? "active" : ""} onClick={() => setMode("register")}>邮箱注册</button>
            <button type="button" role="tab" aria-selected={mode === "login"} className={mode === "login" ? "active" : ""} onClick={() => setMode("login")}>邮箱登录</button>
          </div>
          {mode === "register" ? (
            <label>
              <span>会员名称</span>
              <input value={displayName} maxLength={24} onChange={(event) => setDisplayName(event.target.value)} autoComplete="name" required />
            </label>
          ) : null}
          <label>
            <span>邮箱</span>
            <input type="email" value={identifier} maxLength={254} onChange={(event) => setIdentifier(event.target.value)} autoComplete="username" required />
          </label>
          <label>
            <span>密码</span>
            <input type="password" value={password} minLength={8} maxLength={128} onChange={(event) => setPassword(event.target.value)} autoComplete={mode === "register" ? "new-password" : "current-password"} required />
          </label>
          <button className="button primary" type="submit" disabled={isLoading}>{mode === "register" ? "注册并进入" : "登录并进入"}</button>
          <p className="membership-auth-status" role="status" aria-live="polite">{status}</p>
        </form>
      </section>
    );
  }

  if (user) {
    return (
      <section className="membership-access" aria-labelledby="member-account-heading">
        <div className="membership-account-summary">
          <span className={`membership-tier-badge ${user.membershipTier}`}>{user.membershipTier === "premium" ? "高级会员" : "普通会员"}</span>
          <h3 id="member-account-heading">{user.displayName}</h3>
          <p>{user.email ? `邮箱：${user.email}` : `账号：${user.account}`}</p>
          <button className="button light" type="button" onClick={() => void logoutMembership()} disabled={isLoading}>退出会员账号</button>
        </div>
        <div className="membership-modules" aria-label="水球学院会员板块">
          <div>
            <span>会员板块</span>
            <h3>水球攻防战术</h3>
            <p>无需登录即可浏览和播放基础战术；设计或保存个人战术时再注册登录。</p>
          </div>
          <a className="button primary" href="/member/tactics">
            进入水球攻防战术
            <ArrowRight size={16} />
          </a>
        </div>
      </section>
    );
  }

  return (
    <section className="membership-access" aria-labelledby="membership-form-heading">
      <div className="membership-access-copy">
        <span>Water Polo Academy Membership</span>
        <h3 id="membership-form-heading">{mode === "register" ? "注册水球学院会员" : "登录水球学院会员"}</h3>
        <p>这是整个水球学院网站的统一会员账号。注册或登录后，可以进入已经开放的会员板块。</p>
      </div>
      <div className="membership-auth-form">
        <form className="membership-email-form" onSubmit={(event) => void submitMembership(event)}>
          <div className="membership-auth-tabs" role="tablist" aria-label="邮箱会员账号操作">
            <button type="button" role="tab" aria-selected={mode === "register"} className={mode === "register" ? "active" : ""} onClick={() => setMode("register")}>邮箱注册</button>
            <button type="button" role="tab" aria-selected={mode === "login"} className={mode === "login" ? "active" : ""} onClick={() => setMode("login")}>邮箱登录</button>
          </div>
          {mode === "register" ? (
            <label>
              <span>会员名称</span>
              <input value={displayName} maxLength={24} onChange={(event) => setDisplayName(event.target.value)} autoComplete="name" required />
            </label>
          ) : null}
          <label>
            <span>邮箱</span>
            <input type="email" value={identifier} maxLength={254} onChange={(event) => setIdentifier(event.target.value)} autoComplete="username" required />
          </label>
          <label>
            <span>密码</span>
            <input type="password" value={password} minLength={8} maxLength={128} onChange={(event) => setPassword(event.target.value)} autoComplete={mode === "register" ? "new-password" : "current-password"} required />
          </label>
          <button className="button primary" type="submit" disabled={isLoading}>{mode === "register" ? "使用邮箱注册会员" : "使用邮箱登录"}</button>
          <p className="membership-auth-status" role="status" aria-live="polite">{status}</p>
        </form>
      </div>
    </section>
  );
}

function MemberLoginPage() {
  return (
    <main className="member-login-page">
      <section className="member-login-shell" aria-labelledby="member-login-title">
        <header className="member-login-head">
          <a className="member-login-brand" href="/">
            <img src="/assets/water-polo-academy-emblem-2026.png" alt="" />
            <span>Water <b>Polo</b> Academy</span>
          </a>
          <a className="member-login-close" href="/" aria-label="关闭会员登录">
            <X size={28} />
          </a>
        </header>
        <div className="member-login-title">
          <span>Water Polo Academy Membership</span>
          <h1 id="member-login-title">水球学院会员</h1>
        </div>
        <SiteMembershipAccess compact />
      </section>
    </main>
  );
}

function MembershipPage() {
  const activeSection = businessSectionFromLocation(membershipDetailSections);
  if (activeSection) {
    return <BusinessDetailPage section={activeSection} backHref="/membership" backLabel="返回注册会员" />;
  }

  return (
    <section className="business-page page-card membership-page">
      <div className="club-detail-hero membership-hero">
        <div>
          <span className="business-kicker">付费会员</span>
          <h2>水球学院会员</h2>
          <p>
            注册会员用于承载深度资料、资源筛选、路径咨询和活动优先通知。公开栏目继续提供基础信息，
            更深入的俱乐部筛选、政策材料、海外训练清单和个性化建议将逐步进入会员体系。
          </p>
        </div>
        <div className="relationship-note">
          会员服务为收费服务。具体价格、权益边界、开通方式和服务周期待正式配置后公布。
        </div>
      </div>

      <div className="business-card-grid membership-card-grid">
        {membershipLandingCards.map(({ title, summary, path }) => (
          <a className="business-card membership-card" href={path} key={title}>
            <Users size={21} />
            <strong>{title}</strong>
            <p>{summary}</p>
            <span>
              查看权益
              <ArrowRight size={14} />
            </span>
          </a>
        ))}
      </div>

      <div className="policy-alert membership-note">
        <ShieldCheck size={20} />
        <p>
          会员制的核心不是把信息锁起来，而是把需要人工核验、个性化判断和资源匹配的内容做成更可靠的服务。
          所有涉及升学、海外训练和俱乐部合作的内容仍以公开来源和实际确认结果为准。
        </p>
      </div>
    </section>
  );
}

function YouthWaterPoloPage({
  articles,
  isLoading,
  error
}: {
  articles: Article[];
  isLoading: boolean;
  error: string | null;
}) {
  return (
    <section className="youth-page page-card">
      <div className="desk-header">
        <div>
          <h2>青少年水球</h2>
          <p>青少年赛事、U 系列赛事、俱乐部赛事和青训动态的中文整理入口。</p>
        </div>
        <span className="domestic-count">{isLoading ? "加载中" : `${articles.length} 篇已发布`}</span>
      </div>

      <div className="youth-knowledge-grid">
        <a className="youth-knowledge-card content-entry-card youth-athlete-entry" href="/youth/athletes">
          <div className="youth-knowledge-media">
            <img src="/assets/athlete/home-impact.webp" alt="陈海鲲在水中持球准备进攻" loading="lazy" />
          </div>
          <div className="youth-knowledge-body">
            <h3>青少年运动员</h3>
            <p>记录学员的成长、训练、比赛和心态节奏，让每一段努力都有可以回看的个人档案。</p>
            <div className="youth-fact-list">
              <span>成长档案</span>
              <span>训练与心态</span>
              <span>持续更新</span>
            </div>
            <small className="content-entry-more">
              查看运动员档案
              <ArrowRight size={14} />
            </small>
          </div>
        </a>
        {academyContent.youthSections.map((section) => (
          <a className="youth-knowledge-card content-entry-card" href={youthSectionPath(section)} key={section.id}>
            <div className="youth-knowledge-media">
              <img src={section.image} alt={section.imageAlt} loading="lazy" />
            </div>
            <div className="youth-knowledge-body">
              <h3>{section.title}</h3>
              <p>{section.summary}</p>
              <div className="youth-fact-list">
                {section.facts.map((fact) => (
                  <span key={fact}>{fact}</span>
                ))}
              </div>
              <small className="content-entry-more">
                进入详情
                <ArrowRight size={14} />
              </small>
            </div>
          </a>
        ))}
      </div>

      <ContentCardLibrarySection
        columnId="youth"
        title="青少年参赛路径参考"
        description="把国内 U 系列、俱乐部赛、4 人制和学校赛事整理成家长能判断的路径线索。"
      />

      <div className="youth-published-block">
        <div className="section-heading">
          <div>
            <h2>已发布青少年新闻</h2>
            <p>经来源核验、中文整理和人工发布后，会进入这里。</p>
          </div>
        </div>
        <PublishedArticleCards articles={articles} isLoading={isLoading} error={error} />
      </div>
    </section>
  );
}

function YouthAthletesPage() {
  return (
    <section className="youth-athletes-page page-card">
      <div className="desk-header">
        <div>
          <h2>青少年运动员</h2>
          <p>把真实的训练、比赛与成长记录沉淀为可持续更新的个人数字档案。</p>
        </div>
        <span className="domestic-count">1 位运动员</span>
      </div>

      <div className="youth-athlete-directory" aria-label="青少年运动员档案">
        <a className="youth-athlete-profile" href="/youth/athletes/chen-haikun">
          <img src="/assets/athlete/home-impact.webp" alt="陈海鲲在水中持球准备进攻" loading="lazy" />
          <div className="youth-athlete-profile__copy">
            <span>WATER POLO · 2011 · SHANGHAI</span>
            <h3>陈海鲲</h3>
            <p>从第一次下水到每一次回到水里。这里记录他的成长轨迹、训练分解、心态节奏与影像回放。</p>
            <strong>
              进入个人成长站
              <ArrowRight size={17} />
            </strong>
          </div>
        </a>
      </div>
    </section>
  );
}

function YouthKnowledgeDetailPage({ section }: { section?: YouthKnowledgeSection }) {
  if (!section) {
    return <ContentMissingPage title="没有找到这个青少年水球板块" backHref="/youth" backLabel="返回青少年水球" />;
  }

  return (
    <article className="content-detail-page page-card">
      <ContentDetailHeader
        eyebrow="青少年水球"
        title={section.title}
        summary={section.summary}
        image={section.image}
        imageAlt={section.imageAlt}
        imageSourceName={section.imageSourceName}
        imageSourceUrl={section.imageSourceUrl}
        backHref="/youth"
        backLabel="返回青少年水球"
      />

      <div className="content-detail-grid">
        <div className="content-main-copy">
          <section className="content-detail-group">
            <h3>核心事实</h3>
            <ol>
              {section.facts.map((fact) => (
                <li key={fact}>{fact}</li>
              ))}
            </ol>
          </section>

          {section.detailGroups.map((group) => (
            <section className="content-detail-group" key={group.heading}>
              <h3>{group.heading}</h3>
              <ol>
                {group.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ol>
            </section>
          ))}
        </div>

        <aside className="content-side-panel">
          <SourceLinkList sources={section.sources} />
        </aside>
      </div>
    </article>
  );
}

function ContentDetailHeader({
  eyebrow,
  title,
  summary,
  image,
  imageAlt,
  imageSourceName,
  imageSourceUrl,
  backHref,
  backLabel
}: {
  eyebrow: string;
  title: string;
  summary: string;
  image: string;
  imageAlt: string;
  imageSourceName: string;
  imageSourceUrl: string;
  backHref: string;
  backLabel: string;
}) {
  return (
    <header className="content-detail-header">
      <a className="ghost-control" href={backHref}>
        {backLabel}
      </a>
      <div className="content-detail-hero">
        <div className="content-detail-media">
          <img src={image} alt={imageAlt} />
        </div>
        <div className="content-detail-intro">
          <span>{eyebrow}</span>
          <h2>{title}</h2>
          <p>{summary}</p>
          <a href={imageSourceUrl} target="_blank" rel="noreferrer">
            图片来源：{imageSourceName}
            <ExternalLink size={14} />
          </a>
        </div>
      </div>
    </header>
  );
}

function ContentMissingPage({ title, backHref, backLabel }: { title: string; backHref: string; backLabel: string }) {
  return (
    <section className="page-card content-missing-page">
      <h2>{title}</h2>
      <a className="ghost-control" href={backHref}>
        {backLabel}
      </a>
    </section>
  );
}

function SourceLinkList({ sources, compact = false }: { sources: ContentSourceLink[]; compact?: boolean }) {
  return (
    <div className={`academy-source-list ${compact ? "compact" : ""}`} aria-label="资料来源">
      <strong>资料来源</strong>
      <div>
        {sources.map((source) => (
          <a href={source.url} key={source.url} target="_blank" rel="noreferrer">
            {source.label}
            <ExternalLink size={13} />
          </a>
        ))}
      </div>
    </div>
  );
}

function CalendarPriorityPanel({
  schedules,
  error
}: {
  schedules: FeedItem[];
  error: string | null;
}) {
  const currentYear = new Date().getFullYear();
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  }, [schedules.length]);

  return (
    <aside className="priority-panel">
      <div className="panel-title">
        <div>
          <h2>赛事日历</h2>
        </div>
        <CalendarDays size={22} />
      </div>

      <div className="priority-metrics">
        <div>
          <span>赛程</span>
          <strong>{schedules.length || "—"}</strong>
        </div>
        <div>
          <span>年度</span>
          <strong>{currentYear}</strong>
        </div>
      </div>

      {error ? <div className="alert-line">内容暂不可用：{error}</div> : null}

      <div className="priority-list calendar-priority-list" ref={listRef}>
        {schedules.map((item) => (
          <article key={item.id}>
            <span>赛程</span>
            <strong>{item.title}</strong>
            <small>{formatScheduleSummary(item.summary)}</small>
          </article>
        ))}
        {!schedules.length ? <div className="empty-state dark-empty">暂无赛程条目。</div> : null}
      </div>
    </aside>
  );
}

function EventScheduleDesk({
  title,
  description,
  events,
  schedules,
  notices = [],
  showNotices = false,
  showSchedules = true,
  error,
  action,
  scheduleTitle = "赛程"
}: {
  title: string;
  description?: string;
  events: FeedItem[];
  schedules: FeedItem[];
  notices?: FeedItem[];
  showNotices?: boolean;
  showSchedules?: boolean;
  error?: string | null;
  action?: ReactNode;
  scheduleTitle?: string;
}) {
  return (
    <section className="domestic-section">
      <div className="section-heading">
        <div>
          <h2>{title}</h2>
          {description ? <p>{description}</p> : null}
        </div>
        {action ?? (
          <span className="domestic-count">
            {events.length + notices.length + (showSchedules ? schedules.length : 0)} 条
          </span>
        )}
      </div>

      {error ? <div className="inline-notice">内容服务暂不可用：{error}</div> : null}

      <div className={showSchedules ? "domestic-grid" : "domestic-grid domestic-grid-compact"}>
        <div className="domestic-column">
          <div className="domestic-pane">
            <div className="pane-title">
              <Trophy size={20} />
              <h3>赛事</h3>
            </div>
            <div className="compact-list">
              {events.length ? (
                events.slice(0, 8).map((item) => <FeedRow key={item.id} item={item} />)
              ) : (
                <div className="empty-state">暂无赛事条目。</div>
              )}
            </div>
          </div>

          {showNotices ? (
            <div className="domestic-pane">
              <div className="pane-title">
                <FileText size={20} />
                <h3>通知通告</h3>
              </div>
              <div className="compact-list">
                {notices.length ? (
                  notices.slice(0, 8).map((item) => <FeedRow key={item.id} item={item} />)
                ) : (
                  <div className="empty-state">暂无通知通告。</div>
                )}
              </div>
            </div>
          ) : null}
        </div>

        {showSchedules ? (
          <div className="domestic-pane">
            <div className="pane-title">
              <CalendarDays size={20} />
              <h3>{scheduleTitle}</h3>
            </div>
            <ScheduleList items={schedules.slice(0, 12)} />
          </div>
        ) : null}
      </div>
    </section>
  );
}

function ScheduleList({ items }: { items: FeedItem[] }) {
  return (
    <div className="domestic-calendar-list">
      {items.length ? (
        items.map((item) => (
          <article className="domestic-event calendar-board-item" key={item.id}>
            <strong>{item.title}</strong>
            <span>{item.summary.replace("国内水球赛事日历。", "")}</span>
            <small>{item.sourceName}</small>
          </article>
        ))
      ) : (
        <div className="empty-state">暂无赛程条目。</div>
      )}
    </div>
  );
}

function formatScheduleSummary(summary: string) {
  return summary.replace("国内水球赛事日历。", "");
}

function NoticeDesk({
  notices,
  isLoading,
  error
}: {
  notices: FeedItem[];
  isLoading: boolean;
  error: string | null;
}) {
  return (
    <section className="desk-card page-card">
      <div className="desk-header">
        <div>
          <h2>通知通告</h2>
          <p>{isLoading ? "正在加载通知通告" : `当前收录 ${notices.length} 条通知通告`}</p>
        </div>
      </div>

      {error ? <div className="inline-notice">内容服务暂不可用：{error}</div> : null}

      <div className="news-list">
        {notices.length ? (
          notices.map((item) => <FeedRow key={item.id} item={item} />)
        ) : (
          <div className="empty-state">暂无通知通告。</div>
        )}
      </div>
    </section>
  );
}

function FeedDesk({
  title,
  items,
  totalItems,
  activeCategory = "全部",
  setActiveCategory,
  isLoading,
  error,
  showTabs = true,
  loadingText = "正在加载赛事和赛程",
  countText,
  emptyText = "暂无匹配的赛事或赛程条目。",
  articleBySourceItemId
}: {
  title: string;
  items: FeedItem[];
  totalItems: number;
  activeCategory?: "全部" | "赛事" | "赛程";
  setActiveCategory?: (category: "全部" | "赛事" | "赛程") => void;
  isLoading: boolean;
  error?: string | null;
  showTabs?: boolean;
  loadingText?: string;
  countText?: string;
  emptyText?: string;
  articleBySourceItemId?: Map<string, Article>;
}) {
  return (
    <section className="desk-card page-card">
      <div className="desk-header">
        <div>
          <h2>{title}</h2>
          <p>{isLoading ? loadingText : countText ?? `当前视图 ${totalItems} 条赛事/赛程`}</p>
        </div>
      </div>

      {error ? <div className="inline-notice">内容服务暂不可用：{error}</div> : null}

      {showTabs && setActiveCategory ? (
        <div className="tabs" role="tablist" aria-label="动态分类">
          {categories.map((category) => (
            <button
              key={category}
              className={category === activeCategory ? "active" : ""}
              onClick={() => setActiveCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>
      ) : null}

      <div className="news-list">
        {items.length > 0 ? (
          items.map((item) => (
            <FeedRow
              key={item.id}
              item={item}
              article={articleBySourceItemId?.get(item.id)}
              requireArticleLink={Boolean(articleBySourceItemId)}
            />
          ))
        ) : (
          <div className="empty-state">{emptyText}</div>
        )}
      </div>
    </section>
  );
}

function FeedRow({
  item,
  article,
  requireArticleLink = false
}: {
  item: FeedItem;
  article?: Article;
  requireArticleLink?: boolean;
}) {
  const content = (
    <>
      <time>{item.publishedAt ? formatShortDate(item.publishedAt) : "新收录"}</time>
      <span className="feed-category">{item.category}</span>
      <strong>{article?.title ?? item.title}</strong>
      <span className="feed-source">{article ? "本站新闻" : requireArticleLink ? "待发布" : item.sourceName}</span>
      {article ? <ArrowRight size={16} /> : requireArticleLink ? <span className="feed-pending-dot" aria-hidden="true" /> : <ExternalLink size={16} />}
    </>
  );

  if (article) {
    return (
      <a className="feed-row" href={articlePath(article)}>
        {content}
      </a>
    );
  }

  if (requireArticleLink) {
    return (
      <article className="feed-row feed-row-disabled" aria-label={`${item.title}尚未发布`}>
        {content}
      </article>
    );
  }

  return (
    <a className="feed-row" href={item.url} target="_blank" rel="noreferrer">
      {content}
    </a>
  );
}

function LivePlaceholderPage() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const context = canvas.getContext("2d");
    if (!context) return undefined;

    const canvasElement = canvas;
    const ctx = context;

    const size = 260;
    const center = size / 2;
    const brandBlue = "13, 90, 167";
    const lightBlue = "34, 140, 223";
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function prepareCanvas() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvasElement.width = size * dpr;
      canvasElement.height = size * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function drawDashedRing(radius: number, dash: number[], offset: number, alpha: number, color: string) {
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = `rgb(${color})`;
      ctx.lineWidth = 1;
      ctx.setLineDash(dash);
      ctx.lineDashOffset = offset;
      ctx.beginPath();
      ctx.arc(center, center, radius, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }

    const whirls = [
      { radius: 110, width: 3, length: 1.5, speed: 1.9, direction: 1, alpha: 0.6, color: brandBlue },
      { radius: 120, width: 1.9, length: 1.1, speed: 1.3, direction: -1, alpha: 0.42, color: lightBlue },
      { radius: 100, width: 1.7, length: 2.2, speed: 2.6, direction: 1, alpha: 0.3, color: brandBlue }
    ];

    function drawFrame(time = 0) {
      const seconds = time / 1000;
      ctx.clearRect(0, 0, size, size);

      whirls.forEach((whirl) => {
        const head = (seconds * whirl.speed * whirl.direction) % (Math.PI * 2);
        const steps = 30;

        for (let step = 0; step < steps; step += 1) {
          const fraction = step / steps;
          const angle = head - whirl.direction * fraction * whirl.length;
          const x = center + Math.cos(angle) * whirl.radius;
          const y = center + Math.sin(angle) * whirl.radius;

          ctx.beginPath();
          ctx.globalAlpha = whirl.alpha * (1 - fraction);
          ctx.fillStyle = `rgb(${whirl.color})`;
          ctx.arc(x, y, whirl.width * (1 - fraction * 0.6), 0, Math.PI * 2);
          ctx.fill();
        }
      });

      ctx.globalAlpha = 1;
      drawDashedRing(129, [1, 7], -seconds * 14, 0.16, brandBlue);
      drawDashedRing(90, [1, 9], seconds * 10, 0.12, brandBlue);
    }

    prepareCanvas();
    drawFrame(0);

    if (reducedMotion.matches) {
      return undefined;
    }

    let animationFrame = window.requestAnimationFrame(function animate(time) {
      drawFrame(time);
      animationFrame = window.requestAnimationFrame(animate);
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, []);

  return (
    <section className="live-standby page-card" aria-labelledby="live-standby-title">
      <div className="live-standby-stage">
        <div className="live-standby-loader" role="img" aria-label="赛事直播待机动画">
          <canvas ref={canvasRef} width="260" height="260" />
          <img className="live-standby-logo" src="/assets/live-standby-logo.svg" alt="" />
        </div>
        <div className="live-standby-caption" id="live-standby-title">
          直播即将开始，敬请期待
          <span className="live-standby-dots" aria-hidden="true">
            <b>.</b>
            <b>.</b>
            <b>.</b>
          </span>
        </div>
      </div>
    </section>
  );
}

function EventVideosPage({ videoError, onNavigate }: { videoError: string | null; onNavigate: NavigateHandler }) {
  return (
    <VideoDesk
      videoError={videoError}
      onNavigate={onNavigate}
    />
  );
}

function VideoDesk({
  videoError,
  compact = false,
  onNavigate
}: {
  videoError: string | null;
  compact?: boolean;
  onNavigate: NavigateHandler;
}) {
  const globalVideoCard = videoCards.find((video) => video.category === "global");

  function handleGlobalVideoClick(event: React.MouseEvent<HTMLAnchorElement>) {
    if (!globalVideoCard) return;
    handleNavClick(event, globalVideoCard.page, onNavigate);
  }

  return (
    <section className={`video-section ${compact ? "compact" : ""}`}>
      <div className="section-heading">
        <div>
          <h2>水球赛事视频</h2>
        </div>
        <a className="jump-link" href={globalVideoCard?.url ?? "#"} onClick={handleGlobalVideoClick}>
          进入全球水球赛事
          <ExternalLink size={15} />
        </a>
      </div>

      {videoError ? <div className="inline-notice">视频服务暂不可用：{videoError}</div> : null}

      <div className="video-context video-entry-context" aria-label="水球赛事视频分类">
        <div className="video-grid">
          {videoCards.map((video) => (
            <ExternalVideoCard video={video} key={video.title} onNavigate={onNavigate} />
          ))}
        </div>
      </div>
    </section>
  );
}

function VideoCategoryPage({
  category,
  videos,
  videoError
}: {
  category: VideoCategory;
  videos: DomesticVideo[];
  videoError: string | null;
}) {
  const categoryLabel = videoCategoryLabels[category];

  return (
    <section className="video-section page-card">
      <div className="section-heading">
        <div>
          <h2>{categoryLabel}视频</h2>
        </div>
        <span className="domestic-count">{videos.length ? `${videos.length} 张卡片` : "待更新"}</span>
      </div>

      {videoError ? <div className="inline-notice">视频服务暂不可用：{videoError}</div> : null}

      {videos.length ? (
        <div className="uploaded-video-grid domestic-video-list">
          {videos.map((video) => (
            <UploadedVideoCard video={video} key={video.id} />
          ))}
        </div>
      ) : (
        <div className="empty-state">暂未更新{categoryLabel}视频卡片。</div>
      )}
    </section>
  );
}

function ExternalVideoCard({
  video,
  onNavigate
}: {
  video: (typeof videoCards)[number];
  onNavigate: NavigateHandler;
}) {
  const isInternal = Boolean(video.page);
  function handleInternalClick(event: React.MouseEvent<HTMLAnchorElement>) {
    if (!video.page) return;
    handleNavClick(event, video.page, onNavigate);
  }

  return (
    <a
      href={video.url}
      target={isInternal ? undefined : "_blank"}
      rel={isInternal ? undefined : "noreferrer"}
      className="video-card"
      onClick={isInternal ? handleInternalClick : undefined}
    >
      <span className="video-icon">
        <PlayCircle size={28} />
      </span>
      <strong>{video.title}</strong>
      <span>{video.source}</span>
      <p>{video.summary}</p>
    </a>
  );
}

function UploadedVideoCard({ video }: { video: DomesticVideo }) {
  const categoryLabel = videoCategoryLabels[videoCategoryOf(video)];
  const watchPath = videoWatchPath(video);

  return (
    <article className="uploaded-video-card">
      <a className="uploaded-video-media" href={watchPath} aria-label={`播放${video.title}`}>
        {video.posterUrl ? (
          <img src={video.posterUrl} alt={`${video.title}封面`} loading="lazy" />
        ) : (
          <span className="video-cover-placeholder">
            <FileVideo size={34} />
            <span>视频封面</span>
          </span>
        )}
      </a>
      <div className="uploaded-video-body">
        <span className="video-label">{categoryLabel}</span>
        <h3>{video.title}</h3>
        <p>{video.eventTitle}</p>
        {video.description ? <p>{video.description}</p> : null}
        {video.accessCode ? <span className="video-access-code">播放备注：{video.accessCode}</span> : null}
        <a className="jump-link" href={watchPath}>
          站内播放
          <ExternalLink size={15} />
        </a>
      </div>
    </article>
  );
}

function VideoPlayerPage({
  video,
  videoId,
  videoError,
  isLoading
}: {
  video?: DomesticVideo;
  videoId: string | null;
  videoError: string | null;
  isLoading: boolean;
}) {
  if (videoError) {
    return (
      <section className="video-player-page page-card">
        <div className="inline-notice">视频服务暂不可用：{videoError}</div>
      </section>
    );
  }

  if (isLoading) {
    return (
      <section className="video-player-page page-card">
        <div className="empty-state">正在加载视频...</div>
      </section>
    );
  }

  if (!video || !videoId) {
    return (
      <section className="video-player-page page-card">
        <div className="empty-state">没有找到对应的视频卡片。</div>
      </section>
    );
  }

  return (
    <section className="video-player-page page-card">
      <div className="section-heading">
        <div>
          <h2>{video.title}</h2>
          <p>{video.eventTitle}</p>
        </div>
        <span className="domestic-count">{videoCategoryLabels[videoCategoryOf(video)]}</span>
      </div>

      <CloudVideoPlayer video={video} />

      <div className="video-player-meta">
        {video.description ? <p>{video.description}</p> : null}
        {video.accessCode ? <span className="video-access-code">播放备注：{video.accessCode}</span> : null}
        <small>站内播放器已隐藏下载入口并禁用右键。更强的防下载需要云点播 HLS 加密、短时效签名和防盗链配合。</small>
      </div>
    </section>
  );
}

function ArticlePage({
  article,
  slug,
  articleError,
  isLoading
}: {
  article?: Article;
  slug: string | null;
  articleError: string | null;
  isLoading: boolean;
}) {
  if (articleError) {
    return (
      <section className="article-page page-card">
        <div className="inline-notice">新闻服务暂不可用：{articleError}</div>
      </section>
    );
  }

  if (isLoading) {
    return (
      <section className="article-page page-card">
        <div className="empty-state">正在加载本站新闻...</div>
      </section>
    );
  }

  if (!article || !slug) {
    return (
      <section className="article-page page-card">
        <div className="empty-state">这篇新闻尚未发布，或链接不存在。</div>
      </section>
    );
  }

  return (
    <article className="article-page page-card">
      {article.heroImageUrl ? (
        <ArticleImageFigure article={article} className="article-hero-image" />
      ) : null}

      <header className="article-header">
        <span className="feed-category">{article.category}</span>
        <h1>{article.title}</h1>
        <p>{article.summary}</p>
        <div className="article-meta">
          <span>{article.publishedAt ? `发布：${formatDateTime(article.publishedAt)}` : `更新：${formatDateTime(article.updatedAt)}`}</span>
          <span>来源整理：水球世界</span>
          <span>原始来源：{article.source.sourceName}</span>
        </div>
        {article.tags.length ? (
          <div className="article-tags">
            {article.tags.map((tag) => (
              <span key={tag}>{tag}</span>
            ))}
          </div>
        ) : null}
      </header>

      <div className="article-body">{renderArticleBody(article.body, article)}</div>

      <footer className="article-source-box">
        <div>
          <strong>原始来源</strong>
          <span>{article.source.title}</span>
          <small>
            {article.source.sourceName}
            {article.source.publishedAt ? ` · ${formatDateTime(article.source.publishedAt)}` : ""}
          </small>
        </div>
        <a href={article.source.url} target="_blank" rel="noreferrer">
          查看原始链接
          <ExternalLink size={15} />
        </a>
      </footer>
    </article>
  );
}

function ArticleImageFigure({ article, className }: { article: Article; className: string }) {
  if (!article.heroImageUrl) return null;

  return (
    <figure className={className}>
      <img src={article.heroImageUrl} alt={article.title} />
      <figcaption>
        {article.imageSourceName ? `图片来源：${article.imageSourceName}` : `图片来源：${article.source.sourceName}`}
      </figcaption>
    </figure>
  );
}

function renderArticleBody(body: string, article?: Article) {
  return body.split("\n").map((line, index) => {
    const text = line.trim();
    if (!text) return null;
    const key = `${index}-${text.slice(0, 16)}`;

    if (text.startsWith("## ")) {
      const heading = <h2 key={key}>{text.slice(3)}</h2>;
      if (text === "## 正文" && article?.heroImageUrl) {
        return [
          heading,
          <ArticleImageFigure article={article} className="article-inline-image" key={`${key}-image`} />
        ];
      }

      return heading;
    }

    if (text.startsWith("- ")) {
      return (
        <p className="article-bullet" key={key}>
          <span aria-hidden="true">•</span>
          {text.slice(2)}
        </p>
      );
    }

    return <p key={key}>{text}</p>;
  });
}

function ArticleAdminPage({
  articles,
  articleError,
  isLoading,
  onArticlesChange
}: {
  articles: Article[];
  articleError: string | null;
  isLoading: boolean;
  onArticlesChange: () => Promise<void>;
}) {
  const [selectedId, setSelectedId] = useState("");
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [body, setBody] = useState("");
  const [heroImageUrl, setHeroImageUrl] = useState("");
  const [tagsText, setTagsText] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const selectedArticle = articles.find((article) => article.id === selectedId);
  const draftCount = articles.filter((article) => article.status === "draft").length;
  const publishedCount = articles.filter((article) => article.status === "published").length;

  useEffect(() => {
    if (!articles.length) {
      setSelectedId("");
      return;
    }
    if (!selectedId || !articles.some((article) => article.id === selectedId)) {
      setSelectedId(articles[0].id);
    }
  }, [articles, selectedId]);

  useEffect(() => {
    if (!selectedArticle) {
      setTitle("");
      setSummary("");
      setBody("");
      setHeroImageUrl("");
      setTagsText("");
      return;
    }

    setTitle(selectedArticle.title);
    setSummary(selectedArticle.summary);
    setBody(selectedArticle.body);
    setHeroImageUrl(selectedArticle.heroImageUrl ?? "");
    setTagsText(selectedArticle.tags.join("，"));
  }, [selectedArticle?.id, selectedArticle?.status, selectedArticle?.updatedAt]);

  async function handleGenerateDrafts() {
    setIsGenerating(true);
    setMessage(null);
    setFormError(null);

    try {
      const response = await fetch("/api/articles/generate-drafts", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ refreshFeed: true, limit: 100 })
      });
      const data = (await response.json().catch(() => ({}))) as {
        created?: Article[];
        createdCount?: number;
        skippedCount?: number;
      };
      if (!response.ok) {
        throw new Error(typeof (data as { error?: string }).error === "string" ? (data as { error: string }).error : `API ${response.status}`);
      }

      await onArticlesChange();
      if (data.created?.[0]) {
        setSelectedId(data.created[0].id);
      }
      setMessage(`已生成 ${data.createdCount ?? 0} 篇草稿，跳过 ${data.skippedCount ?? 0} 条已有来源。`);
    } catch (generateError) {
      setFormError(generateError instanceof Error ? generateError.message : "抓取并生成草稿失败");
    } finally {
      setIsGenerating(false);
    }
  }

  async function saveArticle(nextStatus?: Article["status"]) {
    if (!selectedArticle) return;

    setIsSaving(true);
    setMessage(null);
    setFormError(null);

    try {
      const response = await fetch(`/api/articles/${selectedArticle.id}`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          title,
          summary,
          body,
          heroImageUrl,
          tags: tagsText,
          status: nextStatus ?? selectedArticle.status
        })
      });
      const data = (await response.json().catch(() => ({}))) as { article?: Article; error?: string };
      if (!response.ok) {
        throw new Error(typeof data.error === "string" ? data.error : `API ${response.status}`);
      }

      await onArticlesChange();
      if (data.article?.id) {
        setSelectedId(data.article.id);
      }
      setMessage(nextStatus === "published" ? "新闻已发布到前台。" : nextStatus === "draft" ? "新闻已撤回为草稿。" : "草稿已保存。");
    } catch (saveError) {
      setFormError(saveError instanceof Error ? saveError.message : "保存失败");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDeleteArticle() {
    if (!selectedArticle) return;
    if (!window.confirm("确定删除这篇新闻草稿吗？")) return;

    setMessage(null);
    setFormError(null);

    try {
      const response = await fetch(`/api/articles/${selectedArticle.id}`, { method: "DELETE" });
      if (!response.ok) throw new Error(`API ${response.status}`);
      await onArticlesChange();
      setMessage("新闻草稿已删除。");
    } catch (deleteError) {
      setFormError(deleteError instanceof Error ? deleteError.message : "删除失败");
    }
  }

  return (
    <section className="admin-section article-admin-section page-card">
      <div className="section-heading">
        <div>
          <h2>新闻草稿 / 编辑 / 发布</h2>
          <p>抓取公开事实生成草稿，人工核对后发布到本站新闻详情页。</p>
        </div>
        <span className="domestic-count">{draftCount} 草稿 / {publishedCount} 已发布</span>
      </div>

      <div className="storage-banner ready">
        <strong>半自动发布模式</strong>
        <span>系统只生成中文赛事草稿，不会自动上线。发布前请核对事实、日期、队伍名称、版权、图片使用和来源链接。</span>
      </div>

      {articleError ? <div className="inline-notice">新闻服务暂不可用：{articleError}</div> : null}
      {message ? <div className="success-line">{message}</div> : null}
      {formError ? <div className="inline-notice">{formError}</div> : null}

      <div className="article-admin-toolbar">
        <button className="button primary" type="button" onClick={() => void handleGenerateDrafts()} disabled={isGenerating}>
          <WandSparkles size={17} />
          {isGenerating ? "生成中" : "抓取并生成草稿"}
        </button>
        <span>来源重复的条目会自动跳过，已发布内容不会被覆盖。</span>
      </div>

      <div className="article-admin-layout">
        <aside className="article-draft-list" aria-label="新闻草稿列表">
          <div className="video-subheading">
            <h3>新闻库</h3>
            <span>{isLoading ? "加载中" : `${articles.length} 篇`}</span>
          </div>
          {articles.length ? (
            articles.map((article) => (
              <button
                className={`article-draft-item ${article.id === selectedId ? "active" : ""}`}
                type="button"
                key={article.id}
                onClick={() => setSelectedId(article.id)}
              >
                <span className="article-draft-badges">
                  <span className={`status-pill ${article.status}`}>{article.status === "published" ? "已发布" : "草稿"}</span>
                  {article.youthPriority ? <span className="status-pill youth">青少年优先</span> : null}
                </span>
                <strong>{article.title}</strong>
                <small>
                  {article.source.sourceName} · {formatDateTime(article.updatedAt)}
                </small>
              </button>
            ))
          ) : (
            <div className="empty-state">{isLoading ? "正在加载新闻库..." : "还没有新闻草稿。"}</div>
          )}
        </aside>

        <div className="article-editor-panel">
          {selectedArticle ? (
            <form className="admin-form article-editor-form" onSubmit={(event) => {
              event.preventDefault();
              void saveArticle();
            }}>
              <label className="form-field">
                <span>标题</span>
                <input value={title} onChange={(event) => setTitle(event.target.value)} />
              </label>

              <label className="form-field">
                <span>封面图 URL（可选）</span>
                <input value={heroImageUrl} onChange={(event) => setHeroImageUrl(event.target.value)} inputMode="url" placeholder="https://...jpg" />
              </label>

              <label className="form-field form-field-wide">
                <span>摘要</span>
                <textarea value={summary} onChange={(event) => setSummary(event.target.value)} rows={3} />
              </label>

              <label className="form-field form-field-wide">
                <span>正文</span>
                <textarea className="article-body-editor" value={body} onChange={(event) => setBody(event.target.value)} rows={16} />
              </label>

              <label className="form-field form-field-wide">
                <span>标签</span>
                <input value={tagsText} onChange={(event) => setTagsText(event.target.value)} placeholder="水球，赛事，World Aquatics" />
              </label>

              <div className="article-source-panel form-field-wide">
                <strong>来源保留</strong>
                <span>{selectedArticle.source.title}</span>
                <small>{selectedArticle.source.sourceName} · {selectedArticle.source.url}</small>
                <small>图片状态：{articleImageStatusLabel(selectedArticle)}</small>
                {selectedArticle.sourceImageUrl ? <small>原图来源：{selectedArticle.sourceImageUrl}</small> : null}
              </div>

              <div className="admin-form-actions">
                <button className="button primary" type="submit" disabled={isSaving}>
                  <Save size={17} />
                  {isSaving ? "保存中" : "保存草稿"}
                </button>
                {selectedArticle.status === "published" ? (
                  <a className="button light" href={articlePath(selectedArticle)} target="_blank" rel="noreferrer">
                    查看前台
                    <ExternalLink size={16} />
                  </a>
                ) : (
                  <button className="button light" type="button" onClick={() => void saveArticle("published")} disabled={isSaving}>
                    发布
                    <ArrowRight size={16} />
                  </button>
                )}
                {selectedArticle.status === "published" ? (
                  <button className="button light" type="button" onClick={() => void saveArticle("draft")} disabled={isSaving}>
                    撤回为草稿
                  </button>
                ) : null}
                <button className="danger-control" type="button" onClick={() => void handleDeleteArticle()} disabled={isSaving}>
                  <Trash2 size={15} />
                  删除
                </button>
              </div>
            </form>
          ) : (
            <div className="empty-state">请选择一篇新闻草稿，或先抓取并生成草稿。</div>
          )}
        </div>
      </div>
    </section>
  );
}

function CloudVideoPlayer({ video }: { video: DomesticVideo }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [playback, setPlayback] = useState<VideoPlaybackInfo | null>(null);
  const [playbackError, setPlaybackError] = useState<string | null>(null);
  const [isLoadingPlayback, setIsLoadingPlayback] = useState(false);
  const playbackUrl = playback?.playable ? playback.url ?? "" : "";
  const isHlsSource = /\.m3u8(\?|#|$)/i.test(playbackUrl);
  const isDirectVideoSource =
    Boolean(playbackUrl) && (/(\.mp4|\.webm|\.mov|\.m4v)(\?|#|$)/i.test(playbackUrl) || playback?.sourceType === "upload");

  useEffect(() => {
    const controller = new AbortController();
    setPlayback(null);
    setPlaybackError(null);
    setIsLoadingPlayback(true);

    void fetch(`/api/video-playback/${encodeURIComponent(video.id)}`, { signal: controller.signal })
      .then(async (response) => {
        const data = (await response.json().catch(() => ({}))) as { playback?: VideoPlaybackInfo; error?: string };
        if (!response.ok) {
          throw new Error(typeof data.error === "string" ? data.error : `API ${response.status}`);
        }
        setPlayback(data.playback ?? null);
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        setPlaybackError(error instanceof Error ? error.message : "播放地址获取失败");
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoadingPlayback(false);
      });

    return () => controller.abort();
  }, [video.id]);

  useEffect(() => {
    const element = videoRef.current;
    if (!element || !isHlsSource || !playbackUrl) return undefined;

    if (element.canPlayType("application/vnd.apple.mpegurl")) {
      element.src = playbackUrl;
      return undefined;
    }

    let isDisposed = false;
    let cleanup: (() => void) | undefined;

    void import("hls.js").then(({ default: Hls }) => {
      if (isDisposed || !Hls.isSupported()) return;

      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false
      });
      hls.loadSource(playbackUrl);
      hls.attachMedia(element);
      cleanup = () => hls.destroy();
    }).catch(() => {
      if (!isDisposed) setPlaybackError("HLS 播放组件加载失败。");
    });

    return () => {
      isDisposed = true;
      cleanup?.();
    };
  }, [isHlsSource, playbackUrl]);

  if (isLoadingPlayback) {
    return (
      <div className="player-unavailable">
        <FileVideo size={36} />
        <strong>正在准备站内播放器</strong>
        <p>正在获取本次播放地址。</p>
      </div>
    );
  }

  if (playbackError) {
    return (
      <div className="player-unavailable">
        <FileVideo size={36} />
        <strong>播放地址暂不可用</strong>
        <p>{playbackError}</p>
      </div>
    );
  }

  if (playback && !playback.playable) {
    return (
      <div className="player-unavailable">
        <FileVideo size={36} />
        <strong>当前视频需要迁移</strong>
        <p>{playback.reason ?? "该视频还没有配置可站内播放的云端地址，请稍后查看。"}</p>
      </div>
    );
  }

  if (!isHlsSource && !isDirectVideoSource) {
    return (
      <div className="player-unavailable">
        <FileVideo size={36} />
        <strong>当前链接不能站内播放</strong>
        <p>当前视频源暂不支持站内播放，请稍后查看官方回放或已迁移版本。</p>
      </div>
    );
  }

  return (
    <div className="cloud-player-frame" onContextMenu={(event) => event.preventDefault()}>
      <video
        ref={videoRef}
        controls
        controlsList="nodownload noplaybackrate"
        disablePictureInPicture
        playsInline
        preload="metadata"
        poster={video.posterUrl}
        src={isHlsSource ? undefined : playbackUrl}
      />
    </div>
  );
}

function VideoAdminPage({
  targets,
  videos,
  videoStorage,
  videoError,
  onVideosChange
}: {
  targets: FeedItem[];
  videos: DomesticVideo[];
  videoStorage: VideoStorageInfo | null;
  videoError: string | null;
  onVideosChange: () => Promise<void>;
}) {
  const [category, setCategory] = useState<VideoCategory>("domestic");
  const [selectedEventId, setSelectedEventId] = useState("");
  const [eventTitle, setEventTitle] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [existingPosterUrl, setExistingPosterUrl] = useState("");
  const [posterFile, setPosterFile] = useState<File | null>(null);
  const [posterPreviewUrl, setPosterPreviewUrl] = useState("");
  const [accessCode, setAccessCode] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [posterInputKey, setPosterInputKey] = useState(0);
  const [message, setMessage] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingPoster, setIsGeneratingPoster] = useState(false);

  useEffect(() => {
    if (category === "domestic" && !editingId && !selectedEventId && targets.length) {
      setSelectedEventId(targets[0].id);
      setEventTitle(targets[0].title);
      setTitle(targets[0].title);
    }
  }, [category, editingId, selectedEventId, targets]);

  useEffect(() => {
    return () => {
      if (posterPreviewUrl) URL.revokeObjectURL(posterPreviewUrl);
    };
  }, [posterPreviewUrl]);

  const selectedEvent = targets.find((item) => item.id === selectedEventId);
  const editingVideo = videos.find((video) => video.id === editingId);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmedTitle = title.trim();
    const trimmedVideoUrl = videoUrl.trim();
    const trimmedAccessCode = accessCode.trim();
    const resolvedEventTitle =
      category === "domestic" ? selectedEvent?.title || eventTitle.trim() || trimmedTitle : eventTitle.trim() || trimmedTitle;

    if (!trimmedTitle) {
      setFormError("请填写视频标题。");
      return;
    }

    if (!resolvedEventTitle) {
      setFormError("请填写赛事或视频归属。");
      return;
    }

    if (!file && !trimmedVideoUrl && !editingVideo) {
      setFormError("请上传视频文件，或填写云端播放地址。");
      return;
    }

    setIsSaving(true);
    setMessage(null);
    setFormError(null);

    try {
      const formData = new FormData();
      appendFormValue(formData, "id", editingId ?? undefined);
      appendFormValue(formData, "category", category);
      appendFormValue(formData, "eventId", category === "domestic" ? selectedEvent?.id ?? (selectedEventId || undefined) : undefined);
      appendFormValue(formData, "eventTitle", resolvedEventTitle);
      appendFormValue(formData, "title", trimmedTitle);
      appendFormValue(formData, "videoUrl", trimmedVideoUrl || undefined);
      appendFormValue(formData, "accessCode", trimmedAccessCode || undefined);
      appendFormValue(formData, "description", description.trim() || undefined);
      if (file) {
        formData.append("videoFile", file, file.name);
      }
      if (posterFile) {
        formData.append("posterFile", posterFile, posterFile.name);
      }

      const response = await fetch("/api/video-library", {
        method: "POST",
        body: formData
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(typeof data.error === "string" ? data.error : `API ${response.status}`);
      }

      await onVideosChange();
      setEditingId(null);
      setFile(null);
      setPosterFile(null);
      setPosterPreviewUrl("");
      setExistingPosterUrl(typeof data.video?.posterUrl === "string" ? data.video.posterUrl : "");
      setFileInputKey((key) => key + 1);
      setPosterInputKey((key) => key + 1);
      setMessage(file ? "视频已上传并进入站内播放。" : "视频卡片已保存。");
    } catch (saveError) {
      setFormError(saveError instanceof Error ? saveError.message : "保存失败");
    } finally {
      setIsSaving(false);
    }
  }

  function resetVideoForm(nextTitle = "", nextEventTitle = "") {
    setEditingId(null);
    setTitle(nextTitle);
    setEventTitle(nextEventTitle);
    setVideoUrl("");
    setExistingPosterUrl("");
    setPosterFile(null);
    setPosterPreviewUrl("");
    setAccessCode("");
    setDescription("");
    setFile(null);
    setFileInputKey((key) => key + 1);
    setPosterInputKey((key) => key + 1);
    setMessage(null);
    setFormError(null);
  }

  function handlePosterFileChange(nextFile: File | null) {
    setPosterFile(nextFile);
    setPosterPreviewUrl(nextFile ? URL.createObjectURL(nextFile) : "");
    setPosterInputKey((key) => key + 1);
  }

  async function handleGeneratePoster() {
    const directVideoUrl = videoUrl.trim();
    if (!file && !directVideoUrl) {
      setFormError("请先选择视频文件，再生成封面。");
      return;
    }

    setIsGeneratingPoster(true);
    setFormError(null);
    setMessage(null);

    let sourceObjectUrl: string | undefined;
    try {
      const sourceUrl = file ? URL.createObjectURL(file) : directVideoUrl;
      sourceObjectUrl = file ? sourceUrl : undefined;
      const generatedPoster = await generatePosterFileFromVideo(sourceUrl);
      handlePosterFileChange(generatedPoster);
      setExistingPosterUrl("");
      setMessage("已生成封面，保存后会同步到视频卡片。");
    } catch (posterError) {
      setFormError(posterError instanceof Error ? posterError.message : "封面生成失败，请手动上传一张封面图。");
    } finally {
      if (sourceObjectUrl) URL.revokeObjectURL(sourceObjectUrl);
      setIsGeneratingPoster(false);
    }
  }

  function handleCategoryChange(nextCategory: VideoCategory) {
    const nextEvent = nextCategory === "domestic" ? targets[0] : undefined;
    setCategory(nextCategory);
    setSelectedEventId(nextEvent?.id ?? "");
    resetVideoForm(nextEvent?.title ?? "", nextEvent?.title ?? "");
  }

  function handleEventChange(eventId: string) {
    const nextEvent = targets.find((item) => item.id === eventId);
    setSelectedEventId(eventId);
    resetVideoForm(nextEvent?.title ?? "", nextEvent?.title ?? "");
  }

function handleEdit(video: DomesticVideo) {
    const nextCategory = videoCategoryOf(video);
    setCategory(nextCategory);
    setSelectedEventId(nextCategory === "domestic" ? video.eventId : "");
    setEventTitle(video.eventTitle);
    setEditingId(video.id);
    setTitle(video.title);
    setVideoUrl(video.sourceType === "link" ? video.url ?? "" : "");
    setExistingPosterUrl(video.posterUrl ?? "");
    setPosterFile(null);
    setPosterPreviewUrl("");
    setPosterInputKey((key) => key + 1);
    setAccessCode(video.accessCode ?? "");
    setDescription(video.description ?? "");
    setFile(null);
    setFileInputKey((key) => key + 1);
    setMessage(null);
    setFormError(null);
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }

  async function handleDelete(video: DomesticVideo) {
    setMessage(null);
    setFormError(null);

    try {
      const response = await fetch(`/api/video-library/${video.id}`, { method: "DELETE" });
      if (!response.ok) throw new Error(`API ${response.status}`);
      if (editingId === video.id) {
        setEditingId(null);
      }
      await onVideosChange();
      setMessage("视频已删除。");
    } catch (deleteError) {
      setFormError(deleteError instanceof Error ? deleteError.message : "删除失败");
    }
  }

  return (
    <section className="admin-section page-card">
      <div className="section-heading">
        <div>
          <h2>赛事视频卡片后台</h2>
          <p>上传视频或填写云端播放地址，前台点击封面后进入内置播放器。</p>
        </div>
        <span className="domestic-count">{videos.length} 张卡片</span>
      </div>

      <div className={`storage-banner ${videoStorage?.cosConfigured ? "ready" : "warning"}`}>
        <strong>{videoStorage?.cosConfigured ? "腾讯 COS 云存储已配置" : "腾讯 COS 云存储未配置"}</strong>
        <span>
          {videoStorage?.cosConfigured
            ? "后台上传的视频会保存到云端，并通过站内播放器播放。"
            : "线上正式环境未配置云存储时会拒绝文件上传；请先配置腾讯云点播/VOD HLS 加密，或至少配置 COS 后再上传。"}
        </span>
      </div>

      {videoError ? <div className="inline-notice">视频服务暂不可用：{videoError}</div> : null}
      {message ? <div className="success-line">{message}</div> : null}
      {formError ? <div className="inline-notice">{formError}</div> : null}

      <form className="admin-form" onSubmit={handleSubmit}>
        <label className="form-field">
          <span>上传到视频卡片</span>
          <select value={category} onChange={(event) => handleCategoryChange(event.target.value as VideoCategory)}>
            {videoCards.map((card) => (
              <option value={card.category} key={card.category}>
                {card.title}
              </option>
            ))}
          </select>
        </label>

        {category === "domestic" && targets.length ? (
          <label className="form-field">
            <span>关联国内赛事</span>
            <select value={selectedEventId} onChange={(event) => handleEventChange(event.target.value)}>
              {selectedEventId && !selectedEvent ? (
                <option value={selectedEventId}>{eventTitle || "当前已关联赛事"}</option>
              ) : null}
              {targets.map((item) => (
                <option value={item.id} key={item.id}>
                  {item.title}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <label className="form-field">
            <span>赛事/视频归属</span>
            <input
              value={eventTitle}
              onChange={(event) => setEventTitle(event.target.value)}
              placeholder={category === "domestic" ? "例如：全国水球锦标赛" : "例如：World Aquatics 水球世界杯"}
            />
          </label>
        )}

        <label className="form-field">
          <span>视频标题</span>
          <input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="例如：全国冠军赛决赛集锦" />
        </label>

        <label className="form-field">
          <span>云端播放地址（可选）</span>
          <input
            value={videoUrl}
            onChange={(event) => setVideoUrl(event.target.value)}
            placeholder="https://...mp4 或 https://...m3u8"
            inputMode="url"
          />
        </label>

        <label className="form-field">
          <span>上传视频文件（可选）</span>
          <input
            key={fileInputKey}
            type="file"
            accept="video/mp4,video/webm,video/quicktime,video/x-m4v"
            onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          />
        </label>

        <div className="form-field form-field-wide">
          <span>视频封面</span>
          <div className="poster-uploader">
            <div className="poster-preview">
              {posterPreviewUrl || existingPosterUrl ? (
                <img src={posterPreviewUrl || existingPosterUrl} alt="视频封面预览" />
              ) : (
                <span>
                  <FileVideo size={28} />
                  封面预览
                </span>
              )}
            </div>
            <div className="poster-controls">
              <label className="button light poster-file-control">
                <ImagePlus size={17} />
                上传封面图片
                <input
                  key={posterInputKey}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(event) => handlePosterFileChange(event.target.files?.[0] ?? null)}
                />
              </label>
              <button className="button light" type="button" onClick={() => void handleGeneratePoster()} disabled={isGeneratingPoster || (!file && !videoUrl.trim())}>
                <WandSparkles size={17} />
                {isGeneratingPoster ? "生成中" : "从视频生成封面"}
              </button>
              {posterFile ? (
                <button type="button" className="ghost-control" onClick={() => handlePosterFileChange(null)}>
                  取消所选封面
                </button>
              ) : null}
              <small>建议使用 16:9 图片；选择视频文件后可自动截取一帧作为封面。</small>
            </div>
          </div>
        </div>

        <label className="form-field">
          <span>播放备注（可选）</span>
          <input value={accessCode} onChange={(event) => setAccessCode(event.target.value)} placeholder="例如：内部试看 / 加密播放" />
        </label>

        <label className="form-field form-field-wide">
          <span>视频说明</span>
          <textarea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="可填写比赛轮次、队伍、看点或拍摄来源"
            rows={4}
          />
        </label>

        <div className="admin-form-actions">
          <button className="button primary" type="submit" disabled={isSaving}>
            {file ? <UploadCloud size={17} /> : <Save size={17} />}
            {isSaving ? "保存中" : editingId ? "更新视频" : file ? "上传并保存" : "保存卡片"}
          </button>
          {editingId ? (
            <button
              className="button light"
              type="button"
              onClick={() => {
                setEditingId(null);
                setEventTitle(category === "domestic" ? selectedEvent?.title ?? "" : eventTitle);
                setVideoUrl("");
                setExistingPosterUrl("");
                setPosterFile(null);
                setPosterPreviewUrl("");
                setAccessCode("");
                setDescription("");
                setFile(null);
                setFileInputKey((key) => key + 1);
                setPosterInputKey((key) => key + 1);
              }}
            >
              取消编辑
            </button>
          ) : null}
        </div>
      </form>

      <div className="admin-video-list">
        <div className="video-subheading">
          <h3>赛事视频卡片库</h3>
          <span>按更新时间排序</span>
        </div>
        {videos.length ? (
          videos.map((video) => (
            <article className="admin-video-row" key={video.id}>
              <div>
                <strong>{video.title}</strong>
                <span>{video.eventTitle}</span>
                <small>
                  {videoCategoryLabels[videoCategoryOf(video)]} · {videoSourceLabel(video)} · {formatDateTime(video.updatedAt)}
                  {video.accessCode ? ` · ${video.accessCode}` : ""}
                </small>
                {isLegacyNetdiskVideo(video) ? <span className="migration-warning">百度网盘旧链接：需要迁移到云端播放地址</span> : null}
              </div>
              <div className="admin-row-actions">
                <a href={videoWatchPath(video)} className="ghost-control">
                  播放
                </a>
                <button type="button" className="ghost-control" onClick={() => handleEdit(video)}>
                  编辑
                </button>
                <button type="button" className="danger-control" onClick={() => void handleDelete(video)}>
                  <Trash2 size={15} />
                  删除
                </button>
              </div>
            </article>
          ))
        ) : (
          <div className="empty-state">还没有更新过赛事视频卡片。</div>
        )}
      </div>
    </section>
  );
}

function videoCategoryOf(video: DomesticVideo): VideoCategory {
  return video.category === "global" || video.category === "europe" ? video.category : "domestic";
}

function videoSourceLabel(video: DomesticVideo) {
  if (isLegacyNetdiskVideo(video)) return "需迁移";
  if (video.sourceType === "link") return "云端播放";
  if (video.storageProvider === "cos") return "腾讯 COS";
  return "上传文件";
}

function isLegacyNetdiskVideo(video: DomesticVideo) {
  if (!video.url) return false;
  try {
    const host = new URL(video.url, window.location.origin).hostname.toLowerCase();
    return host === "pan.baidu.com" || host.endsWith(".pan.baidu.com");
  } catch {
    return false;
  }
}

function videosForCategory(videos: DomesticVideo[], category: VideoCategory) {
  return videos.filter((video) => videoCategoryOf(video) === category);
}

function handleNavClick(
  event: React.MouseEvent<HTMLAnchorElement>,
  page: PageKey,
  onNavigate: NavigateHandler
) {
  event.preventDefault();
  onNavigate(page);
}

function pageFromLocation(): PageKey {
  const legacyHashPage = pageFromLegacyHash();
  if (legacyHashPage) return legacyHashPage;

  return pageFromPath(window.location.pathname);
}

function businessSectionFromLocation(sections: BusinessDetailSection[]) {
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  return sections.find((section) => section.path === path);
}

function internalPathFromUrl(pathOrUrl: string) {
  try {
    const url = new URL(pathOrUrl, window.location.origin);
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return pathOrUrl;
  }
}

function pageFromPath(pathOrUrl: string): PageKey {
  const pathname = internalPathFromUrl(pathOrUrl).split(/[?#]/)[0];
  const path = pathname.replace(/\/+$/, "") || "/";
  if (path === "/news") return "news";
  if (path === "/youth") return "youth";
  if (path === "/youth/athletes") return "youth-athletes";
  if (path.startsWith("/youth/")) return "youth-detail";
  if (path === "/resources") return "resource-detail";
  if (path.startsWith("/resources/")) return "resource-detail";
  if (path === "/clubs") return "clubs";
  if (path === "/clubs/taiyuan-xuefu") return "club-detail";
  if (path.startsWith("/clubs/")) return "clubs";
  if (path === "/policies" || path.startsWith("/policies/")) return "policies";
  if (path === "/international-training" || path.startsWith("/international-training/")) return "international-training";
  if (path === "/consultation") return "consultation";
  if (path === "/about") return "about";
  if (path === "/membership" || path.startsWith("/membership/")) return "membership";
  if (path === "/member-login") return "member-login";
  if (path.startsWith("/library/")) return "content-card";
  if (path === "/calendar") return "calendar";
  if (path === "/live-competitions") return "live-competitions";
  if (path.startsWith("/videos/watch/")) return "video-player";
  if (path === "/notices") return "notices";
  if (path === "/domestic") return "home";
  if (path === "/global") return "home";
  if (path === "/videos") return "event-videos";
  if (path === "/video") return "video";
  if (path === "/videos/domestic") return "domestic-videos";
  if (path === "/videos/global") return "global-videos";
  if (path === "/videos/europe") return "europe-videos";
  if (path === "/admin/videos") return "video-admin";
  if (path === "/admin/articles") return "article-admin";
  if (path.startsWith("/articles/")) return "article";
  return "home";
}

function canonicalPathFromLocation() {
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  const legacyPathMap: Record<string, string> = {
    "/international-training/clubs": "/international-training/clubs-camps",
    "/international-training/camps": "/international-training/clubs-camps",
    "/camps": "/international-training/clubs-camps"
  };
  return legacyPathMap[path] ?? null;
}

function contentCardPath(card: Pick<ContentCard, "id">) {
  return `/library/${card.id}`;
}

function contentCardIdFromLocation() {
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  return path.startsWith("/library/") ? decodeURIComponent(path.slice("/library/".length)) : null;
}

function contentCardBackHref(card: ContentCard) {
  if (card.columnId === "youth") return "/youth";
  if (card.columnId === "clubs") return "/clubs";
  if (card.columnId === "knowledge") return "/resources/rules";
  if (card.columnId === "policies") return "/policies";
  if (card.columnId === "international-training") return "/international-training/";
  if (card.columnId === "videos") return "/videos";
  return "/news";
}

function contentColumnLabel(columnId: ContentColumnId) {
  const labels: Record<ContentColumnId, string> = {
    news: "赛事资讯",
    youth: "青少年水球",
    clubs: "水球俱乐部",
    knowledge: "水球规则",
    policies: "升学政策咨询",
    "international-training": "国内外训练营",
    videos: "赛事视频"
  };
  return labels[columnId];
}

function verificationStatusLabel(status: ContentCard["verificationStatus"]) {
  const labels: Record<ContentCard["verificationStatus"], string> = {
    confirmed: "已确认",
    sourceVerified: "来源已核验",
    pendingReview: "待进一步核验",
    unverified: "未核验",
    outdated: "可能已过期"
  };
  return labels[status];
}

function riskLevelLabel(level: ContentCard["riskLevel"]) {
  const labels: Record<ContentCard["riskLevel"], string> = {
    low: "低风险",
    medium: "需要核验适用条件",
    high: "高风险，咨询前必须复核"
  };
  return labels[level];
}

function pageFromLegacyHash(): PageKey | null {
  const hash = window.location.hash.replace("#", "");
  if (hash === "news") return "news";
  if (hash === "youth") return "youth";
  if (hash === "resources") return "resource-detail";
  if (hash === "clubs") return "clubs";
  if (hash === "policies") return "policies";
  if (hash === "international-training") return "international-training";
  if (hash === "consultation") return "consultation";
  if (hash === "about") return "about";
  if (hash === "membership") return "membership";
  if (hash === "calendar") return "calendar";
  if (hash === "live-competitions") return "live-competitions";
  if (hash === "notices") return "notices";
  if (hash === "global") return "home";
  if (hash === "domestic") return "home";
  if (hash === "event-videos" || hash === "videos") return "event-videos";
  if (hash === "video") return "video";
  if (hash === "domestic-videos") return "domestic-videos";
  if (hash === "global-videos") return "global-videos";
  if (hash === "europe-videos") return "europe-videos";
  return null;
}

function pathForPage(page: PageKey) {
  return pagePaths[page];
}

function navItemIsActive(item: NavItem, activePage: PageKey): boolean {
  return item.activePages?.includes(activePage) ?? item.key === activePage;
}

function videoWatchPath(video: DomesticVideo) {
  return `/videos/watch/${encodeURIComponent(video.id)}`;
}

function articlePath(article: Article) {
  return `/articles/${encodeURIComponent(article.slug)}`;
}

function resourceSectionPath(section: Pick<AcademySection, "id">) {
  return `/resources/${encodeURIComponent(section.id)}`;
}

function youthSectionPath(section: Pick<YouthKnowledgeSection, "id">) {
  return `/youth/${encodeURIComponent(section.id)}`;
}

function resourceIdFromLocation() {
  const match = window.location.pathname.match(/^\/resources\/([^/]+)\/?$/);
  if (match) return decodeURIComponent(match[1]);
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  return path === "/resources" ? "rules" : null;
}

function youthSectionIdFromLocation() {
  const match = window.location.pathname.match(/^\/youth\/([^/]+)\/?$/);
  return match ? decodeURIComponent(match[1]) : null;
}

function articleImageStatusLabel(article: Article) {
  if (article.imageStatus === "downloaded") return `已下载并加来源水印${article.imageSourceName ? `（${article.imageSourceName}）` : ""}`;
  if (article.imageStatus === "sourced") return `已使用来源网站资料图${article.imageSourceName ? `（${article.imageSourceName}）` : ""}`;
  if (article.imageStatus === "generated") return "已使用本站主题配图";
  if (article.imageStatus === "failed") return "原图抓取失败，发布前建议手动补封面或留空";
  if (article.imageStatus === "missing") return "来源未提供可用主图";
  return article.heroImageUrl ? "已设置封面图" : "未设置封面图";
}

function videoIdFromLocation() {
  const match = window.location.pathname.match(/^\/videos\/watch\/([^/]+)\/?$/);
  return match ? decodeURIComponent(match[1]) : null;
}

function articleSlugFromLocation() {
  const match = window.location.pathname.match(/^\/articles\/([^/]+)\/?$/);
  return match ? decodeURIComponent(match[1]) : null;
}

function generatePosterFileFromVideo(sourceUrl: string): Promise<File> {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    const cleanup = () => {
      video.removeAttribute("src");
      video.load();
    };

    const fail = (message: string) => {
      cleanup();
      reject(new Error(message));
    };

    const capture = () => {
      try {
        const sourceWidth = video.videoWidth;
        const sourceHeight = video.videoHeight;
        if (!sourceWidth || !sourceHeight) {
          fail("没有读取到视频画面，请手动上传一张封面图。");
          return;
        }

        const canvas = document.createElement("canvas");
        canvas.width = 1280;
        canvas.height = 720;
        const context = canvas.getContext("2d");
        if (!context) {
          fail("浏览器暂时不能生成封面，请手动上传一张封面图。");
          return;
        }

        const targetRatio = 16 / 9;
        const sourceRatio = sourceWidth / sourceHeight;
        let sx = 0;
        let sy = 0;
        let sw = sourceWidth;
        let sh = sourceHeight;

        if (sourceRatio > targetRatio) {
          sw = sourceHeight * targetRatio;
          sx = (sourceWidth - sw) / 2;
        } else if (sourceRatio < targetRatio) {
          sh = sourceWidth / targetRatio;
          sy = (sourceHeight - sh) / 2;
        }

        context.drawImage(video, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => {
            cleanup();
            if (!blob) {
              reject(new Error("封面生成失败，请手动上传一张封面图。"));
              return;
            }
            resolve(new File([blob], `poster-${Date.now()}.jpg`, { type: "image/jpeg" }));
          },
          "image/jpeg",
          0.88
        );
      } catch {
        fail("封面生成失败，可能是远程视频不允许截帧；请手动上传一张封面图。");
      }
    };

    video.muted = true;
    video.preload = "metadata";
    video.playsInline = true;
    if (/^https?:\/\//i.test(sourceUrl)) {
      video.crossOrigin = "anonymous";
    }
    video.addEventListener("error", () => fail("视频无法读取，请先选择本地视频文件，或手动上传封面图。"), { once: true });
    video.addEventListener(
      "loadedmetadata",
      () => {
        const duration = Number.isFinite(video.duration) ? video.duration : 0;
        try {
          if (duration > 0.3) {
            video.currentTime = Math.min(Math.max(duration * 0.08, 0.5), duration - 0.1);
          } else {
            capture();
          }
        } catch {
          capture();
        }
      },
      { once: true }
    );
    video.addEventListener("seeked", capture, { once: true });
    video.src = sourceUrl;
    video.load();
  });
}

function isEventOrSchedule(item: FeedItem) {
  return item.category === "赛事" || item.category === "赛程" || item.category === "通知通告";
}

function isDomesticItem(item: FeedItem) {
  return item.sourceId.startsWith("china-") || item.region.includes("中国");
}

function isYouthArticle(article: Article) {
  const haystack = [
    article.title,
    article.summary,
    article.body,
    article.tags.join(" "),
    article.source.title,
    article.source.summary,
    article.source.url
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return /(u\s?1[0-9]|u\s?2[0-3]|under[-\s]?(?:1[0-9]|2[0-3])|youth|junior|juniors|cadet|age group|18u|16u|15u|14u|青少年|青年|少年|u系列|u 系列|俱乐部水球锦标赛)/i.test(
    haystack
  );
}

function pickNearestItem(items: FeedItem[]) {
  const now = Date.now();

  return items
    .map((item, index) => {
      const time = item.publishedAt ? Date.parse(item.publishedAt) : Number.NaN;
      return {
        item,
        index,
        distance: Number.isFinite(time) ? Math.abs(time - now) : Number.POSITIVE_INFINITY
      };
    })
    .sort((a, b) => a.distance - b.distance || a.index - b.index)[0]?.item;
}

function formatShortDate(value: string) {
  return new Intl.DateTimeFormat("zh-CN", {
    month: "2-digit",
    day: "2-digit"
  }).format(new Date(value));
}

function formatDateTime(value: string) {
  return new Intl.DateTimeFormat("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  }).format(new Date(value));
}

function appendFormValue(formData: FormData, key: string, value: string | undefined) {
  if (value) {
    formData.append(key, value);
  }
}

export default App;
